import m from 'mithril'
import client from '@shared/utils/client-rest'
import { getSignalProgressStatus } from '@app/components/optimus-configuration/helpers/optimusHelpers'
import { debounce } from 'lodash'
import {
	validateDateRangeWithPlans,
	extractDateOnly,
} from '@app/components/optimus-configuration/helpers/archive-date-validation'
import { uuid } from 'short-uuid'
import { getCustomerIdFromCurrentRoute } from '@client-shared/utils/users'
import type { ISignalSpecs } from '#frontend/components/optimus-configuration/optimus.interface.ts'
import {
	applyControllerSettingsAcrossSignalsFn,
	applyCoordinationSettingsAcrossSignalsFn,
	applyPhaseSettingsAcrossSignalsFn,
	applySequencesAcrossSignalsFn,
	applyTodsAcrossSignalsFn,
	type IAtomicMovement,
	type IPhaseMapping,
	type IPhaseMappingData,
	type ITmsControllerSetting,
	type ITmsCoordinationSettings,
	type ITmsPhaseSettings,
	type ITmsSequence,
	type ITmsTimeOfDay,
} from '../queries/optimus/optimus-config'
import { fetchNetworkOptimusConfigOnce, type INetworkOptimusConfiguration } from '../queries/optimus/optimus-config-v2'
import type { OperatingMode } from '@shared-schemas/optimus/optimus-config-v2'
import { notifications } from '@client-shared/components-v2/toast'

// Type definitions based on database schemas and API responses
export type PhaseSettingsData = ITmsPhaseSettings
// export interface PhaseSettingsData {
// 	id?: string // Sometimes used as alternative to phase_settings_id
// 	phase_settings_id: string
// 	atlas_signal_id: string
// 	phase_settings_external_id: string
// 	phase_settings_data: Record<string, PhaseData>
// 	unique_setting_key: string
// 	valid_range?: string
// 	valid_from?: string | null
// 	valid_to?: string | null
// 	status?: string
// 	error?: string | null
// }

export interface PhaseData {
	enabled: boolean
	maxSplit?: number | null
	minSplit: number | null
	coordinated: boolean
	pedestrianPhase: boolean
	dummyPhase: boolean
	minimumGreen: number | null
	vehicleExt: number | null
	redClearance: number | null
	allowLeftTurn: boolean
	minPedWalkTime: number | null
	minPedClearance: number | null
	yellowClearance: number | null
}

export type CoordinationSettingsData = ITmsCoordinationSettings

export interface CoordinationData {
	cycle: number
	offset: number
	offsetReferencePoint: string
	// Absent on slices with no covering coordination plan, and on rows written before the key existed.
	mode?: OperatingMode
	splits: Record<string, number>
	phaseSequence: {
		ring_1?: number[]
		ring_2?: number[]
	}
	activePhases: number[]
	clock_drift_adjustment: boolean
}

export interface ControllerData {
	forceOffMode?: 'float' | 'fixed' | null
	offsetReferencePoint?: string | null
}

export type TimeOfDayData = ITmsTimeOfDay

export interface TodScheduleData {
	days: {
		friday: boolean
		monday: boolean
		sunday: boolean
		tuesday: boolean
		saturday: boolean
		thursday: boolean
		wednesday: boolean
	}
	months: {
		january: boolean
		february: boolean
		march: boolean
		april: boolean
		may: boolean
		june: boolean
		july: boolean
		august: boolean
		september: boolean
		october: boolean
		november: boolean
		december: boolean
	}
	days_of_month: Record<string, boolean>
	mode: 'coordinated' | 'free' | 'actuated-free' | 'flash'
	local_end_time: string
	local_start_time: string
}

export type PhaseMappingData = IPhaseMappingData
export type PhaseMapping = IPhaseMapping
export type AtomicMovement = IAtomicMovement

export type SequenceNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

export type ValidRingSequence = [SequenceNumber, SequenceNumber, SequenceNumber, SequenceNumber]

export type SequenceData = ITmsSequence

export type ControllerSettingsData = ITmsControllerSetting

export interface NetworkData {
	id: string
	name: string
	region_id: string
	customer_id: string
	enabled_in_sig_ops_mode: boolean
	enabled_in_safety_mode: boolean
	timezone_iana: string
}

export interface SignalData {
	id: string
	name: string
	name_short?: string
	signalStatus?: string
	network_id: string
}

interface IApplyAllChangesResults {
	success: boolean
	failedSignals: string[]
	successfulSignals: string[]
	deletionResults: {
		tods: { success: boolean; count: number }
		phase_settings: { success: boolean; count: number }
		coordination_settings: { success: boolean; count: number }
		phase_mappings: { success: boolean; count: number }
		sequences: { success: boolean; count: number }
	}
}
interface PhaseSettingsArchiveRequest {
	phase_settings_id: string
	current_range: {
		valid_from: string | null
		valid_to: string | null
	}
	new_range: {
		valid_from: string
		valid_to: string
	}
}
interface CoordinationSettingsArchiveRequest {
	coordination_settings_id: string
	current_range: {
		valid_from: string | null
		valid_to: string | null
	}
	new_range: {
		valid_from: string
		valid_to: string
	}
}

interface TimeOfDayArchiveRequest {
	time_of_day_id: string
	current_range: {
		valid_from: string | null
		valid_to: string | null
	}
	new_range: {
		valid_from: string
		valid_to: string
	}
}

export const OFFSET_REFERENCE_POINT_ENUM = {
	lead: 'lead',
	lag: 'lag',
	// yield: 'yield',
	yellow: 'yellow',
	'ring-1': 'ring-1',
} as const

export const COORDINATION_MODE_ENUM = {
	coordinated: 'coordinated',
	free: 'free',
	'actuated-free': 'actuated-free',
	flash: 'flash',
} as const

export const FORCE_OFF_MODE_ENUM = {
	float: 'float',
	fixed: 'fixed',
} as const

export const SIGNAL_PROGRESS_STATE_ENUM = {
	'not-started': 'not-started',
	'in-progress-no-errors': 'in-progress-no-errors',
	'in-progress-errors': 'in-progress-errors',
	complete: 'complete',
} as const

// API endpoints
// const API_ENDPOINTS = {
// 	NETWORKS: "/api/v1/entities/networks",
// 	SIGNALS: "/api/v1/entities/signals",
// 	PHASE_SETTINGS: "/api/v1/entities/phase-settings",
// 	COORDINATION_SETTINGS: "/api/v1/entities/coordination-settings",
// 	TOD_SCHEDULES: "/api/v1/entities/tod-schedules",
// 	PHASE_MAPPINGS: "/api/v1/entities/phase-mappings",
// };

// Specific error types based on validation functions
export interface ValidationError {
	message: string
	errorType: string
}

// Phase Settings Errors - keyed by phase setting ID and error type
export interface PhaseSettingsErrors {
	[phaseSettingsId: string]: {
		[errorKey: string]: string // e.g., "1-yellowClearance": "Phase 1: Invalid Yellow Clearance Value"
	}
}

// Coordination Settings Errors - keyed by coordination setting ID and error type
export interface CoordinationSettingsErrors {
	[errorKey: string]: string // e.g., "coord-123-cycle": "Plan # 1: Cycle - Value must be positive"
}

// Controller Settings Errors - keyed by controller settings ID and error type
export interface ControllerSettingsErrors {
	[errorKey: string]: string
}

// Time of Day Errors - keyed by TOD ID and error type
export interface TimeOfDayErrors {
	[errorKey: string]: string // e.g., "tod-456-start": "Morning Schedule - Start overlap detected"
}

// Phase Mapping Errors - keyed by phase mapping ID and error type
export interface PhaseMappingErrors {
	[errorKey: string]: string // e.g., "mapping-789_phases_in_phase_mapping_but_not_phase_plan": "Phase Plan 1: Phases [3,4] in Phase Mapping do not exist in Phase Plan."
}
export interface SequenceErrors {
	[errorKey: string]: string // e.g., "mapping-789_phases_in_phase_mapping_but_not_phase_plan": "Phase Plan 1: Phases [3,4] in Phase Mapping do not exist in Phase Plan."
}

// TMS Errors - from backend validation
export interface TmsErrors {
	[errorKey: string]: {
		timing_difference: number | null
		is_clock_drift?: boolean
		actionable?: boolean
		labels: string[]
		tod_ids: string[]
		message?: string | null
		type?: string | null
	}
}

// Completed Configuration Errors - from cross-validation
export interface CompletedConfigurationErrors {
	[errorKey: string]: string // e.g., "tod-configuration-fully-used": "A TOD must be used across all signals"
}

export interface ErrorData {
	errors:
		| PhaseSettingsErrors
		| CoordinationSettingsErrors
		| ControllerSettingsErrors
		| TimeOfDayErrors
		| PhaseMappingErrors
		| SequenceErrors
		| TmsErrors
		| CompletedConfigurationErrors
	count: number
}

export interface SignalErrors {
	'phase-settings': {
		errors: PhaseSettingsErrors
		count: number
	}
	'coordination-settings': {
		errors: CoordinationSettingsErrors
		count: number
	}
	'controller-settings': {
		errors: ControllerSettingsErrors
		count: number
	}
	'tod-schedule': {
		errors: TimeOfDayErrors
		count: number
	}
	'phase-mapping': {
		errors: PhaseMappingErrors
		count: number
	}
	sequences: {
		errors: SequenceErrors
		count: number
	}
	'tms-errors': {
		errors: TmsErrors
		count: number
		progress: {
			completed: number
			total: number
			inProgress: number
			timestamp: string
		} | null
	}
	'completed-configurations': {
		errors: CompletedConfigurationErrors
		count: number
	}
	'configuration-detials': any // Typo in original code - keeping for compatibility
	totalCount: number
}

export interface ApplyResult {
	success: boolean
	totalSignals: number
	successCount: number
	failedSignals: string[]
	stillFailedSignals?: string[]
}

// Replace your IAutoSave with this
interface IAutoSave {
	delay_ms: number
	max_retries: number
	enabled: boolean
	timers: Map<string, number> // timeout id from window.setTimeout
	in_flight: Set<string> // signals currently saving
	needs_another_pass: Set<string>
}

// Add this minimal result type used below
interface SaveResult {
	success: boolean
	message?: string
	error?: unknown
	skipped?: boolean
	queued?: boolean
}

interface IUpdatePhaseSettingsRes {
	updated: PhaseSettingsData[]
}
interface IUpdateCoordinationSettingsRes {
	updated: CoordinationSettingsData[]
}
interface IUpdateControllerSettingsRes {
	updated: ControllerSettingsData[]
}
interface IUpdateSequencesRes {
	updated: SequenceData[]
}

interface IUpdateTimeOfDayRes {
	updated: TimeOfDayData[]
}
interface IUpdatePhaseMappingRes {
	updated: PhaseMappingData[]
}

export interface ITmsValidationResults {
	tod_id: string
	status: 'completed' | 'error' | 'in_progress'
	requestId: string
	data?: {
		force_off_flag: boolean
		cycle_flag: boolean
		split_flag: boolean | null
		offset_flag: boolean | null
		force_off_data_source: string
		cycle_data_source: string
		suspected_clock_drift_or_wrong_offset: boolean
		suspected_wrong_split_or_wrong_sequence: boolean
		suspected_drift_in_seconds: number
		force_off_analysis: {
			[key: string]: {
				tms_force_off_index: number | null
				probe_force_off_index: number | null
				atspm_force_off_index: number | null
				tms_probe_difference: number | null
				tms_atspm_difference: number | null
			}
		}
		cycle_analysis: {
			probe_alert_cycle: boolean
			tms_cycle: number | null
			tms_split: number | null
			atspm_split: number | null
			atspm_offset: number | null
			tms_offset: number | null
			estimated_cycle: number | null
			atspm_cycle: null
			atspm_alert_cycle: boolean
			cycle_flag: boolean
			probe_distribution: {
				[key: string]: number
			}
			atspm_distribution: {}
		}
		split_analysis: {}
		offset_analysis: {
			atspm_distribution: {}
			atspm_offset: number | null
			tms_offset: number | null
		}
		start_date: string
		end_date: string
	}
	error: {
		message: string
		type: string
	} | null
	completedAt?: string
}

export interface ITmsBucket {
	errors: TmsErrors
	count: number
	progress: {
		completed: number
		total: number
		inProgress: number
		timestamp: string
	} | null
}

interface ISignalBaseState {
	timestamp?: number
	action?: string
	tab?: string | null
	selected_network: string | null
	selected_signal: string | null
	phase_settings: {
		[key: string]: PhaseSettingsData[]
	}
	coordination_settings: {
		[key: string]: CoordinationSettingsData[]
	}
	controller_settings: {
		[key: string]: ControllerSettingsData[]
	}
	tod_schedules: {
		[key: string]: TimeOfDayData[]
	}
	phase_mappings: {
		[key: string]: PhaseMappingData[]
	}
	sequences: {
		[key: string]: SequenceData[]
	}
	signals_with_pending_changes: string[]
	tods_to_delete: string[]
	phase_settings_to_delete: string[]
	coordination_settings_to_delete: string[]
	controller_settings_to_delete: string[]
	phase_mappings_to_delete: string[]
	sequences_to_delete: string[]
}
// ─── New-schema → legacy-model mapping ─────────────────────────────────────────
// The report flow and the legacy config page read per-signal settings in the original shape
// (atlas_signal_id / *_external_id / valid_range / FK-linked TODs). The network config now comes
// from the assembled-config endpoint (signal_id / plan_number / plan_start_date / tod_groups with
// array-typed days/months/dom). These helpers reshape the new structure back into the legacy
// per-signal arrays so the existing validation + eligibility logic keeps working unchanged.

const MODEL_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const MODEL_MONTHS = [
	'january',
	'february',
	'march',
	'april',
	'may',
	'june',
	'july',
	'august',
	'september',
	'october',
	'november',
	'december',
] as const

// upper(valid_range) for a version = the next version's plan_start_date for the same plan_number
// (per signal), or null for the latest. Returns each row annotated with its derived {valid_from, valid_to}.
function annotateValidRanges<T extends { plan_number: string; plan_start_date: string }>(rows: T[]) {
	const rows_by_plan = new Map<string, T[]>()
	for (const row of rows) {
		const list = rows_by_plan.get(row.plan_number) ?? []
		list.push(row)
		rows_by_plan.set(row.plan_number, list)
	}
	const annotated: Array<T & { valid_from: string; valid_to: string | null }> = []
	for (const versions of rows_by_plan.values()) {
		const sorted = [...versions].sort(
			(a, b) => new Date(a.plan_start_date).getTime() - new Date(b.plan_start_date).getTime(),
		)
		sorted.forEach((row, idx) => {
			annotated.push({
				...row,
				valid_from: row.plan_start_date,
				valid_to: idx + 1 < sorted.length ? sorted[idx + 1].plan_start_date : null,
			})
		})
	}
	return annotated
}

const toValidRange = (valid_from: string, valid_to: string | null) => `[${valid_from},${valid_to ?? ''})`

const daysArrayToObject = (days: string[]) =>
	MODEL_DAYS.reduce(
		(acc, day) => {
			acc[day] = days.includes(day)
			return acc
		},
		{} as Record<string, boolean>,
	)

const monthsArrayToObject = (months: string[]) => {
	const all = months.length === 0
	return MODEL_MONTHS.reduce(
		(acc, month) => {
			acc[month] = all || months.includes(month)
			return acc
		},
		{} as Record<string, boolean>,
	)
}

const domsArrayToObject = (doms: number[]) => {
	const all = doms.length === 0
	const acc: Record<string, boolean> = {}
	for (let day = 1; day <= 31; day++) acc[String(day)] = all || doms.includes(day)
	return acc
}

// Reshape one signal from the assembled network config into the six legacy per-signal arrays.
function mapNewConfigSignalToLegacy(signal: INetworkOptimusConfiguration[number]) {
	const sid = signal.signal_id

	// plan_number → resolved row id, used to relink TODs / phase mappings to their settings rows.
	const phase_id_by_plan = new Map(signal.phase_settings.map((ps) => [ps.plan_number, ps.phase_settings_id]))
	const coord_id_by_plan = new Map(
		signal.coordination_settings.map((cs) => [cs.plan_number, cs.coordination_settings_id]),
	)
	const sequence_id_by_plan = new Map(signal.sequences.map((s) => [s.plan_number, s.sequence_id]))

	const phase_settings = annotateValidRanges(signal.phase_settings).map((ps) => ({
		phase_settings_id: ps.phase_settings_id,
		phase_settings_external_id: ps.plan_number,
		atlas_signal_id: sid,
		phase_settings_data: ps.phase_settings_data,
		valid_range: toValidRange(ps.valid_from, ps.valid_to),
		valid_from: ps.valid_from,
		valid_to: ps.valid_to,
	}))

	const coordination_settings = annotateValidRanges(signal.coordination_settings).map((cs) => ({
		coordination_settings_id: cs.coordination_settings_id,
		coordination_settings_external_id: cs.plan_number,
		atlas_signal_id: sid,
		coordination_settings_data: cs.coordination_settings_data,
		valid_range: toValidRange(cs.valid_from, cs.valid_to),
		valid_from: cs.valid_from,
		valid_to: cs.valid_to,
	}))

	const controller_settings = annotateValidRanges(signal.controller_settings).map((cs) => ({
		controller_settings_id: cs.controller_settings_id,
		controller_settings_external_id: cs.plan_number,
		atlas_signal_id: sid,
		controller_settings_data: cs.controller_settings_data,
		valid_range: toValidRange(cs.valid_from, cs.valid_to),
		valid_from: cs.valid_from,
		valid_to: cs.valid_to,
	}))

	const sequences = annotateValidRanges(signal.sequences).map((seq) => ({
		sequence_id: seq.sequence_id,
		sequence_external_id: seq.plan_number,
		atlas_signal_id: sid,
		sequence_data: seq.sequence_data,
		valid_range: toValidRange(seq.valid_from, seq.valid_to),
		valid_from: seq.valid_from,
		valid_to: seq.valid_to,
	}))

	const phase_mappings = signal.phase_mapping.map((pm) => ({
		atlas_phase_mapping_id: pm.phase_mapping_id,
		phase_settings_id: phase_id_by_plan.get(pm.plan_number) ?? '',
		phase_settings_external_id: pm.plan_number,
		atlas_signal_id: sid,
		phase_mapping: pm.phase_mapping.map((phase) => ({
			...phase,
			atomic_movements: (phase.atomic_movements ?? []).map((movement, idx) => ({
				...movement,
				key: `${movement.edge_connection_id}-${idx}`,
			})),
			key: `${pm.phase_mapping_id}-${phase.number}`,
		})),
	}))

	// Flatten tod_groups → time_of_days in the legacy shape: relink FK ids from the referenced
	// plan_number and reshape the parent group's day/month/dom arrays into boolean maps.
	const tod_schedules = []
	for (const group of annotateValidRanges(signal.tod_groups)) {
		const days = daysArrayToObject(group.days_of_week)
		const months = monthsArrayToObject(group.months)
		const days_of_month = domsArrayToObject(group.days_of_month)
		for (const tod of group.time_of_days) {
			tod_schedules.push({
				time_of_day_id: tod.time_of_day_id,
				atlas_signal_id: sid,
				name: tod.name,
				time_of_day_data: {
					days,
					months,
					days_of_month,
					mode: '',
					local_start_time: tod.local_start_time,
					local_end_time: tod.local_end_time,
				},
				phase_settings_id: tod.phase_plan_number != null ? (phase_id_by_plan.get(tod.phase_plan_number) ?? '') : '',
				coordination_settings_id:
					tod.coordination_plan_number != null ? (coord_id_by_plan.get(tod.coordination_plan_number) ?? null) : null,
				sequence_id: tod.sequence_plan_number != null ? (sequence_id_by_plan.get(tod.sequence_plan_number) ?? null) : null,
				phase_settings_external_id: tod.phase_plan_number ?? '',
				coordination_settings_external_id: tod.coordination_plan_number ?? null,
				sequence_external_id: tod.sequence_plan_number ?? null,
				valid_range: toValidRange(group.valid_from, group.valid_to),
				valid_from: group.valid_from,
				valid_to: group.valid_to,
				local_start_time: tod.local_start_time,
				local_end_time: tod.local_end_time,
			})
		}
	}

	return { phase_settings, coordination_settings, controller_settings, sequences, phase_mappings, tod_schedules }
}

/**
 * Model for managing signal configuration data for Optimus
 * Handles loading, saving, and managing configuration data for signals
 */
class OptimusConfigurationModel {
	// Loading and error states
	is_loading: boolean = false
	is_saving: boolean = false
	save_success: boolean = false

	// Selection state
	selected_network: string | null = null
	selected_signal: string | null = null
	active_tab: string = 'phase' // Default tab: 'phase', 'coordination', 'tod', or 'phase-mapping'

	// Toggle: when true, the TOD schedule shows the Months and Days-of-Month columns
	// (otherwise hidden, with all-year/all-days defaults). Lives on the model so the
	// schedule body and the header (which renders the toggle button alongside "Add New TOD")
	// can read/write the same value.
	show_advanced_tod_edit: boolean = false

	// Data collections
	networks: NetworkData[] = []
	signals: ISignalSpecs[] = []
	phase_settings: Record<string, PhaseSettingsData[]> = {}
	coordination_settings: Record<string, CoordinationSettingsData[]> = {}
	controller_settings: Record<string, ControllerSettingsData[]> = {}
	sequences: Record<string, SequenceData[]> = {}

	tod_schedules: Record<string, TimeOfDayData[]> = {}
	phase_mappings: Record<string, PhaseMappingData[]> = {}

	// BEGIN HACK - All document versions (not deduped by ID) are needed for Generate Timing Plan UX (notably, around Job Request Modal).
	// TODO: Check sync with above canonical data collections
	phase_settings_all_versions: Record<string, PhaseSettingsData[]> = {}
	coordination_settings_all_versions: Record<string, CoordinationSettingsData[]> = {}
	controller_settings_all_versions: Record<string, ControllerSettingsData[]> = {}

	tod_schedules_all_versions: Record<string, TimeOfDayData[]> = {}
	phase_mappings_all_versions: Record<string, PhaseMappingData[]> = {}
	// END HACK

	// State for tracking pending changes
	signals_with_pending_changes: Set<string> = new Set()

	_debouncedTmsValidationMap: Map<string, any> = new Map()
	_tmsPollers: Map<string, any> = new Map()

	// Deletion tracking arrays for bulk delete operations
	tods_to_delete: string[] = []
	phase_settings_to_delete: string[] = []
	coordination_settings_to_delete: string[] = []
	controller_settings_to_delete: string[] = []
	phase_mappings_to_delete: string[] = []
	sequences_to_delete: string[] = []

	// Track newly created plans from "Set Upcoming Plan" modal
	newly_created_plans: Set<string> = new Set()

	// Track original TOD schedule data (day ranges AND start/end times) to detect changes
	original_tod_data: { [signalId: string]: { [todId: string]: any } } = {}

	// Track which TODs have already been converted to avoid multiple delete+recreate cycles
	converted_tods: Set<string> = new Set()

	// Track signals that are currently being saved to prevent concurrent saves
	signals_being_saved: Set<string> = new Set()

	// Track when each signal's data was last loaded from the server (for cache TTL)
	private signalLoadTimestamps: Record<string, number> = {}

	// Track which signals we've already emitted ghost mapping logs for (to avoid spam)
	private _ghostMappingLoggedSignals: Set<string> = new Set()

	/**
	 * Detect and log any Phase Mappings whose phase_settings_id does not exist in phase_settings
	 * Logs once per signal per refresh to avoid excessive noise in prod
	 */
	private _logGhostPhaseMappings(signalId: string): void {
		try {
			const plans = this.phase_settings?.[signalId] || []
			const planIds = new Set((plans || []).map((p: any) => String(p.phase_settings_id)))
			const mappings = this.phase_mappings?.[signalId] || []
			const ghosts = (mappings || []).filter((pm: any) => !planIds.has(String(pm?.phase_settings_id)))
			if (ghosts.length && !this._ghostMappingLoggedSignals.has(signalId)) {
				this._ghostMappingLoggedSignals.add(signalId)
				const ghostPlanIds = Array.from(new Set(ghosts.map((g: any) => String(g?.phase_settings_id))))
				console.warn(
					'[Optimus PhaseMapping] Detected ghost Phase Mapping(s) — phase_settings_id not found in phase_settings',
					{
						signalId,
						count: ghosts.length,
						phase_settings_ids: ghostPlanIds,
					},
				)
			}
		} catch (e) {
			console.warn('[Optimus PhaseMapping] Ghost detection failed', { signalId, error: e })
		}
	}

	tms_error_progress: {
		[key: string]: {
			is_loading: boolean
			total_count: number
			completed: number
		}
	} = {}
	// Errors
	signal_errors: Record<string, SignalErrors> = {}

	// Undo functionality
	undo_history: any[] = []
	session_start_state: any = null
	page_start_states: Record<string, any> = {} // Store initial state for each tab/page
	max_undo_history: number = 50 // Limit history to prevent memory issues
	needs_state_capture: boolean = false // Flag to indicate if we need to capture current state before next edit

	// Force-off and cycle length validation data (mock for demonstration)
	probe_data: Record<string, any> = {} // Will store probe data for validation

	// Autosave data
	autosave: IAutoSave = {
		delay_ms: 3000,
		max_retries: 2,
		enabled: false,
		timers: new Map(),
		in_flight: new Set(),
		needs_another_pass: new Set(),
	}

	// Note: TOD autosave disabled due to add/delete complexity

	// in class OptimusConfigurationModel
	is_saving_by_signal: Record<string, boolean> = {}
	last_saved_at: Record<string, number> = {}
	_dirty_revision: Record<string, number> = {}

	// Callback to notify UI components that data has been refreshed
	notifyDataRefresh?: () => void

	constructor() {
		// Constructor is now empty since properties are initialized above
	}

	/**
	 * Fetch networks for a region
	 * @param regionId - The ID of the region
	 * @returns The fetched networks
	 */
	async fetchRegionSpecs(regionId: string): Promise<NetworkData[]> {
		this.is_loading = true
		m.redraw()

		try {
			const customer_id = getCustomerIdFromCurrentRoute()
			// Use the same endpoint pattern as in the regions.js model
			const response = await client(`/api/v1/models/regions/${regionId}/specs`, {
				params: {
					...(customer_id ? { customer_id } : {}),
				},
			})

			// Extract networks from the response
			if (response && response.networks) {
				this.networks = response.networks || []
			} else {
				console.error('Invalid response format:', response)
				this.networks = []
			}

			return this.networks
		} catch (error) {
			console.error('Failed to fetch networks:', error)
			this.networks = []
			throw error
		} finally {
			this.is_loading = false
			m.redraw()
		}
	}

	generateKey() {
		return crypto.randomUUID?.() || Math.random().toString(36).slice(2)
	}

	/**
	 * Deep clone a state object, properly handling Sets and other complex types
	 * @param {Object} state - The state object to clone
	 * @returns {Object} - The cloned state object
	 */
	deepCloneState(state: ISignalBaseState) {
		return {
			timestamp: state.timestamp,
			action: state.action,
			tab: state.tab,
			selected_network: state.selected_network,
			selected_signal: state.selected_signal,
			phase_settings: JSON.parse(JSON.stringify(state.phase_settings)),
			coordination_settings: JSON.parse(JSON.stringify(state.coordination_settings)),
			controller_settings: JSON.parse(JSON.stringify(state.controller_settings)),
			tod_schedules: JSON.parse(JSON.stringify(state.tod_schedules)),
			phase_mappings: JSON.parse(JSON.stringify(state.phase_mappings)),
			sequences: JSON.parse(JSON.stringify(state.sequences)),
			signals_with_pending_changes: Array.from(state.signals_with_pending_changes || []),
			tods_to_delete: [...(state.tods_to_delete || [])],
			phase_settings_to_delete: [...(state.phase_settings_to_delete || [])],
			coordination_settings_to_delete: [...(state.coordination_settings_to_delete || [])],
			controller_settings_to_delete: [...(state.controller_settings_to_delete || [])],
			phase_mappings_to_delete: [...(state.phase_mappings_to_delete || [])],
			sequences_to_delete: [...(state.sequences_to_delete || [])],
		}
	}

	/**
	 * Capture the current state for undo functionality
	 * @param {string} action - Description of the action being performed
	 * @param {string} tab - The tab/page where the action occurred
	 */
	captureStateForUndo(action: string, tab: string | null = null) {
		const currentState: ISignalBaseState = {
			timestamp: Date.now(),
			action: action,
			tab: tab,
			selected_network: this.selected_network,
			selected_signal: this.selected_signal,
			phase_settings: JSON.parse(JSON.stringify(this.phase_settings)),
			coordination_settings: JSON.parse(JSON.stringify(this.coordination_settings)),
			controller_settings: JSON.parse(JSON.stringify(this.controller_settings)),
			sequences: JSON.parse(JSON.stringify(this.sequences)),
			tod_schedules: JSON.parse(JSON.stringify(this.tod_schedules)),
			phase_mappings: JSON.parse(JSON.stringify(this.phase_mappings)),
			signals_with_pending_changes: Array.from(this.signals_with_pending_changes),
			tods_to_delete: [...this.tods_to_delete],
			phase_settings_to_delete: [...this.phase_settings_to_delete],
			coordination_settings_to_delete: [...this.coordination_settings_to_delete],
			controller_settings_to_delete: [...this.controller_settings_to_delete],
			phase_mappings_to_delete: [...this.phase_mappings_to_delete],
			sequences_to_delete: [...this.sequences_to_delete],
		}

		// Add to history
		this.undo_history.push(currentState)

		// Reset the needs_state_capture flag after capturing state
		// This ensures we capture the current state before the next edit
		if (this.needs_state_capture) {
			this.needs_state_capture = false
			// Trigger a redraw to update UI components that depend on undo availability
			m.redraw()
		}

		// Limit history size
		if (this.undo_history.length > this.max_undo_history) {
			this.undo_history.shift()
		}

		// Capture session start state if not already captured
		if (!this.session_start_state) {
			this.session_start_state = this.deepCloneState(currentState)
			//	signal: this.selected_signal,
			//	network: this.selected_network,
			//	phaseSettingsSignals: Object.keys(currentState.phase_settings || {}).length,
			//	coordSettingsSignals: Object.keys(currentState.coordination_settings || {}).length,
			//	todSignals: Object.keys(currentState.tod_schedules || {}).length,
			//	phaseMappingsSignals: Object.keys(currentState.phase_mappings || {}).length,
			//});
		}

		// Page start states are now captured when signal is selected
		// This ensures they capture the original state before any edits
		if (tab && !this.page_start_states[tab]) {
			this.page_start_states[tab] = this.deepCloneState(currentState)
		}
	}

	/**
	 * Restore state from a given state object
	 * @param {Object} state - The state to restore
	 */
	restoreState(state: ISignalBaseState) {
		this.phase_settings = JSON.parse(JSON.stringify(state.phase_settings || {}))
		this.coordination_settings = JSON.parse(JSON.stringify(state.coordination_settings || {}))
		this.controller_settings = JSON.parse(JSON.stringify(state.controller_settings || {}))
		this.tod_schedules = JSON.parse(JSON.stringify(state.tod_schedules || {}))
		this.phase_mappings = JSON.parse(JSON.stringify(state.phase_mappings || {}))
		this.sequences = JSON.parse(JSON.stringify(state.sequences || {}))

		// Handle signals_with_pending_changes - convert from Array back to Set
		const pendingChanges = state.signals_with_pending_changes || []
		this.signals_with_pending_changes = new Set(Array.isArray(pendingChanges) ? pendingChanges : [])

		this.tods_to_delete = [...(state.tods_to_delete || [])]
		this.phase_settings_to_delete = [...(state.phase_settings_to_delete || [])]
		this.coordination_settings_to_delete = [...(state.coordination_settings_to_delete || [])]
		this.controller_settings_to_delete = [...(state.controller_settings_to_delete || [])]
		this.phase_mappings_to_delete = [...(state.phase_mappings_to_delete || [])]
		this.sequences_to_delete = [...(state.sequences_to_delete || [])]
	}

	/**
	 * Restore state for a specific page/tab only
	 * @param {string} tab - The tab to restore
	 * @param {Object} state - The state to restore from
	 */
	restorePageState(tab: string, state: ISignalBaseState) {
		if (!this.selected_signal) {
			return
		}

		// Only restore the data for the specific tab
		switch (tab) {
			case 'phase-settings':
				this.phase_settings[this.selected_signal] = JSON.parse(
					JSON.stringify(state.phase_settings[this.selected_signal] || []),
				)
				break
			case 'coordination-settings':
				this.coordination_settings[this.selected_signal] = JSON.parse(
					JSON.stringify(state.coordination_settings[this.selected_signal] || []),
				)
				break
			case 'controller-settings':
				this.controller_settings[this.selected_signal] = JSON.parse(
					JSON.stringify(state.controller_settings[this.selected_signal] || []),
				)
				break
			case 'tod-schedule':
				this.tod_schedules[this.selected_signal] = JSON.parse(
					JSON.stringify(state.tod_schedules[this.selected_signal] || []),
				)
				break
			case 'phase-mapping':
				this.phase_mappings[this.selected_signal] = JSON.parse(
					JSON.stringify(state.phase_mappings[this.selected_signal] || []),
				)
				break
			case 'sequences':
				this.sequences[this.selected_signal] = JSON.parse(JSON.stringify(state.sequences[this.selected_signal] || []))
				break
			default:
				return
		}
	}

	/**
	 * Check if a specific page/tab has changes compared to its start state
	 * @param {string} tab - The tab to check
	 * @param {Object} pageStartState - The page start state to compare against
	 * @returns {boolean} - True if the page has changes, false otherwise
	 */
	isPageStateChanged(tab: string, pageStartState: ISignalBaseState) {
		if (!this.selected_signal) {
			return false
		}

		// Compare only the data relevant to this specific tab
		switch (tab) {
			case 'phase-settings':
				const currentPhaseSettings = this.phase_settings[this.selected_signal] || []
				const startPhaseSettings = pageStartState.phase_settings[this.selected_signal] || []
				return !this.isDataEqual(currentPhaseSettings, startPhaseSettings)

			case 'coordination-settings':
				const currentCoordSettings = this.coordination_settings[this.selected_signal] || {}
				const startCoordSettings = pageStartState.coordination_settings[this.selected_signal] || {}
				return !this.isDataEqual(currentCoordSettings, startCoordSettings)

			case 'controller-settings':
				const currentControllerSettings = this.controller_settings[this.selected_signal] || []
				const startControllerSettings = pageStartState.controller_settings[this.selected_signal] || []
				return !this.isDataEqual(currentControllerSettings, startControllerSettings)

			case 'tod-schedule':
				const currentTodSchedules = this.tod_schedules[this.selected_signal] || []
				const startTodSchedules = pageStartState.tod_schedules[this.selected_signal] || []
				return !this.isDataEqual(currentTodSchedules, startTodSchedules)

			case 'phase-mapping':
				const currentPhaseMappings = this.phase_mappings[this.selected_signal] || []
				const startPhaseMappings = pageStartState.phase_mappings[this.selected_signal] || []
				return !this.isDataEqual(currentPhaseMappings, startPhaseMappings)

			case 'sequences':
				const currentSequences = this.sequences[this.selected_signal] || []
				const startSequences = pageStartState.sequences[this.selected_signal] || []
				return !this.isDataEqual(currentSequences, startSequences)

			default:
				return false
		}
	}

	isDataEqual(obj1: Object, obj2: Object) {
		return JSON.stringify(obj1) === JSON.stringify(obj2)
	}

	undoLatestEdit() {
		if (this.undo_history.length < 1) {
			return false // Need at least 1 state to restore to
		}

		// Remove the state we just restored from history
		this.undo_history.pop()

		// The last state in history is the "before" state we want to restore to
		const stateToRestore = this.undo_history[this.undo_history.length - 1]

		this.restoreState(stateToRestore)

		const signal_id = this.selected_signal
		if (signal_id) {
			// Cancel any pending autosave timers for this signal
			const t = this.autosave?.timers?.get?.(signal_id)
			if (t !== undefined) {
				window.clearTimeout(t)
				this.autosave.timers.delete(signal_id)
			}
			// Do not queue another autosave pass from a prior in-flight save
			this.autosave?.needs_another_pass?.delete?.(signal_id)
			// Mark dirty so UI knows changes exist, but do NOT schedule autosave here
			this.signals_with_pending_changes.add(signal_id)
			this._bumpDirty?.(signal_id)
		}

		return true
	}

	undoAllEditsOnPage(tab: string) {
		if (!this.page_start_states[tab]) {
			return false
		}

		this.restorePageState(tab, this.page_start_states[tab])
		// Capture the current state after page restore for undo history
		this.captureStateForUndo(`Restored page: ${tab}`, tab)

		const signal_id = this.selected_signal
		if (signal_id) {
			// Clear delete tracking for this page/signal so "Undo all edits on this page"
			// truly removes both edits and pending deletes.
			this.clearDeleteListsForPageAndSignal(tab, signal_id)

			// Cancel any pending autosave timers for this signal
			const t = this.autosave?.timers?.get?.(signal_id)
			if (t !== undefined) {
				window.clearTimeout(t)
				this.autosave.timers.delete(signal_id)
			}
			// Do not queue another autosave pass from a prior in-flight save
			this.autosave?.needs_another_pass?.delete?.(signal_id)
			// Mark dirty so UI knows changes exist, but do NOT schedule autosave here
			this.signals_with_pending_changes.add(signal_id)
			this._bumpDirty?.(signal_id)
		}

		return true
	}

	undoAllEditsInSession() {
		if (!this.session_start_state) {
			console.warn('[Optimus Undo] undoAllEditsInSession: no session_start_state; aborting')
			return false
		}

		this.restoreState(this.session_start_state)
		// Reset all undo tracking
		this.undo_history = [this.deepCloneState(this.session_start_state)]
		this.page_start_states = {}

		const signal_id = this.selected_signal
		if (signal_id) {
			this.signals_with_pending_changes.add(signal_id)
			this._bumpDirty?.(signal_id)
			this.scheduleAutoSave?.(signal_id, 'all')
		}

		return true
	}

	/**
	 * Get the current state for comparison
	 * @returns {Object} - Current state object
	 */
	getCurrentState(): ISignalBaseState {
		const currentState: ISignalBaseState = {
			selected_network: this.selected_network,
			selected_signal: this.selected_signal,
			phase_settings: this.phase_settings,
			coordination_settings: this.coordination_settings,
			controller_settings: this.controller_settings,
			tod_schedules: this.tod_schedules,
			phase_mappings: this.phase_mappings,
			sequences: this.sequences,
			signals_with_pending_changes: Array.from(this.signals_with_pending_changes),
			tods_to_delete: [...this.tods_to_delete],
			phase_settings_to_delete: [...this.phase_settings_to_delete],
			coordination_settings_to_delete: [...this.coordination_settings_to_delete],
			controller_settings_to_delete: [...this.controller_settings_to_delete],
			phase_mappings_to_delete: [...this.phase_mappings_to_delete],
			sequences_to_delete: [...this.sequences_to_delete],
		}

		return currentState
	}

	/**
	 * Compare two states for equality (ignoring timestamp and action)
	 * @param {Object} state1 - First state to compare
	 * @param {Object} state2 - Second state to compare
	 * @returns {boolean} - True if states are equal
	 */
	isStateEqual(state1: ISignalBaseState, state2: ISignalBaseState) {
		if (!state1 || !state2) {
			return false
		}

		// Compare the important state properties
		const isEqual =
			state1.selected_network === state2.selected_network &&
			state1.selected_signal === state2.selected_signal &&
			JSON.stringify(state1.phase_settings) === JSON.stringify(state2.phase_settings) &&
			JSON.stringify(state1.coordination_settings) === JSON.stringify(state2.coordination_settings) &&
			JSON.stringify(state1.controller_settings) === JSON.stringify(state2.controller_settings) &&
			JSON.stringify(state1.tod_schedules) === JSON.stringify(state2.tod_schedules) &&
			JSON.stringify(state1.phase_mappings) === JSON.stringify(state2.phase_mappings) &&
			JSON.stringify(state1.sequences) === JSON.stringify(state2.sequences) &&
			JSON.stringify(state1.signals_with_pending_changes) === JSON.stringify(state2.signals_with_pending_changes) &&
			JSON.stringify(state1.tods_to_delete) === JSON.stringify(state2.tods_to_delete) &&
			JSON.stringify(state1.phase_settings_to_delete) === JSON.stringify(state2.phase_settings_to_delete) &&
			JSON.stringify(state1.sequences_to_delete) === JSON.stringify(state2.sequences_to_delete) &&
			JSON.stringify(state1.coordination_settings_to_delete) ===
				JSON.stringify(state2.coordination_settings_to_delete) &&
			JSON.stringify(state1.controller_settings_to_delete) === JSON.stringify(state2.controller_settings_to_delete) &&
			JSON.stringify(state1.phase_mappings_to_delete) === JSON.stringify(state2.phase_mappings_to_delete)

		return isEqual
	}

	/**
	 * Check if undo operations are available
	 * @returns {Object} - Object indicating which undo operations are available
	 */
	getUndoAvailability() {
		const latestEditAvailable = this.undo_history.length >= 2

		const result = {
			latestEdit: latestEditAvailable,
			pageEdits: (tab: string) => {
				if (!this.page_start_states[tab] || !this.selected_signal) {
					return false
				}
				// Check if current state is different from page start state for this specific tab
				const isDifferent = this.isPageStateChanged(tab, this.page_start_states[tab])
				return isDifferent
			},
			sessionEdits: () => {
				if (!this.session_start_state) {
					return false
				}
				// Check if current state is different from session start state
				const isDifferent = !this.isStateEqual(this.getCurrentState(), this.session_start_state)
				return isDifferent
			},
		}

		return result
	}

	/**
	 * Start a fresh undo session (e.g., when Optimus Configuration modal opens).
	 * Sets the session_start_state to the current state and resets history to that snapshot.
	 */
	startUndoSession(): void {
		// Reset session tracking; initial snapshot will be captured after first data load/capture
		this.session_start_state = null
		this.undo_history = []
		this.page_start_states = {}
	}

	/**
	 * End the current undo session (e.g., when the modal closes).
	 * Clears undo tracking without altering the actual configuration data.
	 */
	endUndoSession(): void {
		this.undo_history = []
		this.page_start_states = {}
		this.session_start_state = null
	}

	/**
	 * Reset all in-memory state related to the Optimus Configuration modal.
	 * Called when the modal unmounts so the next open starts from a clean slate
	 * and forces data to be refetched from the server.
	 */
	resetModalState(): void {
		// Cancel any pending autosave timers without triggering network saves
		for (const timerId of Array.from(this.autosave.timers.values())) {
			window.clearTimeout(timerId)
		}
		this.autosave.timers = new Map()
		this.autosave.in_flight = new Set()
		this.autosave.needs_another_pass = new Set()

		// Core loading/saving flags
		this.is_loading = false
		this.is_saving = false
		this.save_success = false

		// Selection state
		this.selected_network = null
		this.selected_signal = null
		this.active_tab = 'phase'

		// Data collections
		this.networks = []
		this.signals = []
		this.phase_settings = {}
		this.coordination_settings = {}
		this.tod_schedules = {}
		this.phase_mappings = {}
		this.sequences = {}

		// Pending changes / delete tracking
		this.signals_with_pending_changes.clear()
		this.clearDeleteLists()

		// New plan / TOD tracking
		this.newly_created_plans.clear()
		this.original_tod_data = {}
		this.converted_tods.clear()

		// Saving / cache tracking
		this.signals_being_saved.clear()
		this.signalLoadTimestamps = {}
		this._ghostMappingLoggedSignals.clear()

		// TMS validation state
		this.tms_error_progress = {}
		this.signal_errors = {}
		this.destroyAllTmsPoller()

		// Undo / probe state
		this.endUndoSession()
		this.needs_state_capture = false
		this.probe_data = {}

		// Autosave bookkeeping
		this.is_saving_by_signal = {}
		this.last_saved_at = {}
		this._dirty_revision = {}

		// UI callbacks
		this.notifyDataRefresh = undefined

		// Clear all_versions data (used by job request modal)
		this.phase_settings_all_versions = {}
		this.coordination_settings_all_versions = {}
		this.controller_settings_all_versions = {}
		this.tod_schedules_all_versions = {}
		this.phase_mappings_all_versions = {}
	}

	/**
	 * Get the tab name for a given type
	 * @param {string} type - Type of item ('tod', 'phase_setting', 'coordination_setting', 'phase_mapping')
	 * @returns {string} - The tab name
	 */
	getTabForType(type: string) {
		switch (type) {
			case 'tod':
				return 'tod-schedule'
			case 'phase_setting':
				return 'phase-settings'
			case 'coordination_setting':
				return 'coordination-settings'
			case 'phase_mapping':
				return 'phase-mapping'
			default:
				return null
		}
	}

	/**
	 * Build a stable compound deletion key for a specific version of an item
	 */
	private makeDeleteKey(
		id: string,
		unique_row_key: string,
		valid_from?: string | null,
		valid_to?: string | null,
	): string {
		const from = valid_from || ''
		const to = valid_to == null ? 'infinity' : valid_to
		return `${id}::${from}::${to}::${unique_row_key}`
	}

	/** Parse a compound deletion key back into parts */
	private parseDeleteKey(key: string): {
		id: string
		unique_row_key: string
		valid_from: string | null
		valid_to: string | null
	} {
		const [id, from, to, unique_row_key] = key.split('::')
		return { id, valid_from: from || null, valid_to: to || null, unique_row_key }
	}

	/**
	 * Add a specific version (id + valid_from + valid_to) to the deletion tracking list
	 * @param {string} type - 'tod' | 'phase_setting' | 'coordination_setting' | 'phase_mapping'
	 * @param {string} id - Unique item id
	 * @param {string|null|undefined} valid_from - Version start
	 * @param {string|null|undefined} valid_to - Version end (use 'infinity' when open)
	 */
	addToDeleteList(
		type: string,
		id: string,
		unique_key: string,
		valid_from?: string | null,
		valid_to?: string | null,
		should_cancel_autosave?: boolean,
	) {
		if (!id) return
		const key = this.makeDeleteKey(id, unique_key, valid_from || null, valid_to || null)

		switch (type) {
			case 'tod':
				if (!this.tods_to_delete.includes(key)) this.tods_to_delete.push(key)
				break
			case 'phase_setting':
				if (!this.phase_settings_to_delete.includes(key)) this.phase_settings_to_delete.push(key)
				break
			case 'coordination_setting':
				if (!this.coordination_settings_to_delete.includes(key)) this.coordination_settings_to_delete.push(key)
				break
			case 'phase_mapping':
				if (!this.phase_mappings_to_delete.includes(key)) this.phase_mappings_to_delete.push(key)
				break
			case 'sequences':
				if (!this.sequences_to_delete.includes(key)) this.sequences_to_delete.push(key)
				break
		}

		// Capture state AFTER making changes
		this.captureStateForUndo(`Mark ${type} for deletion`, this.getTabForType(type))

		const signal_id = this.selected_signal
		if (signal_id && !should_cancel_autosave) {
			this._bumpDirty?.(signal_id)
			this.scheduleAutoSave?.(signal_id, type)
		}
	}

	/**
	 * Remove a specific version from the deletion tracking list
	 */
	removeFromDeleteList(
		type: string,
		id: string,
		unique_key: string,
		valid_from?: string | null,
		valid_to?: string | null,
	) {
		if (!id) return
		const key = this.makeDeleteKey(id, unique_key, valid_from || null, valid_to || null)

		switch (type) {
			case 'tod':
				this.tods_to_delete = this.tods_to_delete.filter((k) => k !== key)
				break
			case 'phase_setting':
				this.phase_settings_to_delete = this.phase_settings_to_delete.filter((k) => k !== key)
				break
			case 'coordination_setting':
				this.coordination_settings_to_delete = this.coordination_settings_to_delete.filter((k) => k !== key)
				break
			case 'phase_mapping':
				this.phase_mappings_to_delete = this.phase_mappings_to_delete.filter((k) => k !== key)
				break
			case 'sequences':
				this.sequences_to_delete = this.sequences_to_delete.filter((k) => k !== key)
				break
		}

		// Capture state AFTER making changes
		this.captureStateForUndo(`Unmark ${type} for deletion`, this.getTabForType(type))
	}

	/**
	 * Check if a specific version is marked for deletion (id + valid_from + valid_to)
	 */
	isMarkedForDeletion(
		type: string,
		id: string,
		unique_key: string,
		valid_from?: string | null,
		valid_to?: string | null,
	) {
		if (!id) return false
		const key = this.makeDeleteKey(id, unique_key, valid_from || null, valid_to || null)

		switch (type) {
			case 'tod':
				return this.tods_to_delete.includes(key)
			case 'phase_setting':
				return this.phase_settings_to_delete.includes(key)
			case 'coordination_setting':
				return this.coordination_settings_to_delete.includes(key)
			case 'phase_mapping':
				return this.phase_mappings_to_delete.includes(key)
			case 'sequences':
				return this.sequences_to_delete.includes(key)
			default:
				return false
		}
	}

	/**
	 * Clear deletion tracking entries for a given tab/signal only.
	 * This is used by undoAllEditsOnPage so that page-level undo
	 * truly removes both edits and pending deletes for that page.
	 */
	private clearDeleteListsForPageAndSignal(tab: string, signalId: string): void {
		const pageState: any = this.page_start_states?.[tab]
		if (!pageState) return

		const signalKey = String(signalId)

		const removeKeysForIds = (keys: string[], ids: Set<string>): string[] =>
			keys.filter((key) => {
				const { id } = this.parseDeleteKey(key)
				return !ids.has(id)
			})

		if (tab === 'coordination-settings') {
			const original = pageState.coordination_settings?.[signalKey] || []
			const ids = new Set((original as any[]).map((c: any) => c.coordination_settings_id).filter(Boolean))
			if (ids.size > 0) {
				this.coordination_settings_to_delete = removeKeysForIds(this.coordination_settings_to_delete, ids)
			}
		} else if (tab === 'phase-settings') {
			const original = pageState.phase_settings?.[signalKey] || []
			const ids = new Set((original as any[]).map((p: any) => p.phase_settings_id).filter(Boolean))
			if (ids.size > 0) {
				this.phase_settings_to_delete = removeKeysForIds(this.phase_settings_to_delete, ids)
			}
		} else if (tab === 'controller-settings') {
			const original = pageState.controller_settings?.[signalKey] || []
			const ids = new Set((original as any[]).map((c: any) => c.controller_settings_id || c.id).filter(Boolean))
			if (ids.size > 0) {
				this.controller_settings_to_delete = removeKeysForIds(this.controller_settings_to_delete, ids)
			}
		} else if (tab === 'tod-schedule') {
			const original = pageState.tod_schedules?.[signalKey] || []
			const ids = new Set((original as any[]).map((t: any) => t.id || t.time_of_day_id).filter(Boolean))
			if (ids.size > 0) {
				this.tods_to_delete = removeKeysForIds(this.tods_to_delete, ids)
			}
		} else if (tab === 'phase-mapping') {
			const original = pageState.phase_mappings?.[signalKey] || []
			const ids = new Set((original as any[]).map((m: any) => m.atlas_phase_mapping_id).filter(Boolean))
			if (ids.size > 0) {
				this.phase_mappings_to_delete = removeKeysForIds(this.phase_mappings_to_delete, ids)
			}
		} else if (tab === 'sequences') {
			const original = pageState.sequences?.[signalKey] || []
			const ids = new Set((original as any[]).map((m: any) => m.id || m.phase_settings_id).filter(Boolean))
			if (ids.size > 0) {
				this.sequences_to_delete = removeKeysForIds(this.sequences_to_delete, ids)
			}
		}
	}

	/**
	 * Clear all deletion tracking arrays
	 */
	clearDeleteLists() {
		this.tods_to_delete = []
		this.phase_settings_to_delete = []
		this.coordination_settings_to_delete = []
		this.controller_settings_to_delete = []
		this.phase_mappings_to_delete = []
		this.sequences_to_delete = []
	}

	/**
	 * Process bulk deletions for all tracked items
	 * @param {Object} results - Results object to update with deletion status
	 */
	async processBulkDeletions(results: IApplyAllChangesResults) {
		// Delete TOD schedules using new endpoint pattern
		if (this.tods_to_delete.length > 0) {
			try {
				// Separate TODs into those that need API calls vs direct deletion
				const todsForApi: string[] = []
				const todsForDirectDeletion: string[] = []

				for (const key of this.tods_to_delete) {
					const { id: todId } = this.parseDeleteKey(key)
					const isNewOrDefault = todId?.startsWith('new-') || todId?.startsWith('default-')

					if (isNewOrDefault) {
						todsForDirectDeletion.push(key)
					} else {
						todsForApi.push(key)
					}
				}

				// Handle direct deletion for new/default TODs
				if (todsForDirectDeletion.length > 0) {
					this.removeDeletedTodsFromUI(todsForDirectDeletion)
					results.deletionResults.tods.count += todsForDirectDeletion.length
				}

				// Handle API deletion for regular TODs
				if (todsForApi.length > 0) {
					// Group TODs by signal for the new endpoint structure
					const todsBySignal: { [signalId: string]: any[] } = {}

					for (const key of todsForApi) {
						const { id: todId, unique_row_key, valid_from, valid_to } = this.parseDeleteKey(key)
						// Find the signal that contains this specific TOD version
						let signalId: string | null = null
						for (const [sId, tods] of Object.entries(this.tod_schedules)) {
							const found = (tods || []).find((t) => {
								const tId = t.id || t.time_of_day_id
								const tFrom = t.valid_from || null
								const tTo = t.valid_to == null ? 'infinity' : t.valid_to
								return tId === todId && (tFrom || null) === (valid_from || null) && tTo === (valid_to || 'infinity')
							})
							if (found) {
								signalId = sId
								break
							}
						}
						if (signalId) {
							if (!todsBySignal[signalId]) todsBySignal[signalId] = []
							todsBySignal[signalId].push({
								time_of_day_id: todId,
								valid_from: valid_from || null,
								valid_to: valid_to || 'infinity',
							})
						}
					}

					// Delete TODs for each signal
					let totalDeleted = 0
					for (const [signalId, deleteData] of Object.entries(todsBySignal)) {
						const response = await client(`/api/v1/pages/tms/time-of-days/signal/${signalId}/delete`, {
							method: 'POST',
							body: deleteData,
						})

						if (response.deleted && response.deleted.length > 0) {
							totalDeleted += response.deleted.length
						}
					}

					results.deletionResults.tods.count += totalDeleted
					// Remove deleted TODs from the UI lists
					this.removeDeletedTodsFromUI(todsForApi)
				}

				this.tods_to_delete = []
			} catch (error) {
				console.error('Failed to delete TOD schedules:', error)
				results.deletionResults.tods.success = false
				results.success = false
			}
		}

		// Delete phase settings using new endpoint pattern
		if (this.phase_settings_to_delete.length > 0) {
			try {
				// Separate phase settings into those that need API calls vs direct deletion
				const phaseSettingsForApi: string[] = []
				const phaseSettingsForDirectDeletion: string[] = []

				for (const key of this.phase_settings_to_delete) {
					const { id: phaseSettingId } = this.parseDeleteKey(key)
					const isNewOrDefault = phaseSettingId?.startsWith('new-') || phaseSettingId?.startsWith('default-')

					if (isNewOrDefault) {
						phaseSettingsForDirectDeletion.push(key)
					} else {
						phaseSettingsForApi.push(key)
					}
				}

				// Handle direct deletion for new/default phase settings
				if (phaseSettingsForDirectDeletion.length > 0) {
					this.removeDeletedPhaseSettingsFromUI(phaseSettingsForDirectDeletion)
					results.deletionResults.phase_settings.count += phaseSettingsForDirectDeletion.length
				}

				// Handle API deletion for regular phase settings
				if (phaseSettingsForApi.length > 0) {
					// Group phase settings by signal for the new endpoint structure
					const phaseSettingsBySignal: { [signalId: string]: any[] } = {}

					for (const key of phaseSettingsForApi) {
						const {
							id: phaseSettingId,
							unique_row_key,
							valid_from: keyValidFrom,
							valid_to: keyValidTo,
						} = this.parseDeleteKey(key)

						// Find the signal that contains this specific phase setting version
						let signalId: string | null = null
						for (const [sId, phases] of Object.entries(this.phase_settings)) {
							const found = (phases || []).find((p) => {
								const pId = p.phase_settings_id
								const pFrom = p.valid_from || null
								const pTo = p.valid_to == null ? 'infinity' : p.valid_to
								return (
									pId === phaseSettingId &&
									(pFrom || null) === (keyValidFrom || null) &&
									pTo === (keyValidTo || 'infinity')
								)
							})
							if (found) {
								signalId = sId as string
								break
							}
						}

						if (signalId) {
							if (!phaseSettingsBySignal[signalId]) {
								phaseSettingsBySignal[signalId] = []
							}

							// For deletion, always use the original date range from page_start_states
							// so we target the row as it exists in the database, not any edited/archive range.
							let originalFrom: string | null = null
							let originalTo: string | null = null

							const phaseStartState = this.page_start_states?.['phase-settings'] as ISignalBaseState | undefined
							if (phaseStartState?.phase_settings) {
								for (const originalPhases of Object.values(phaseStartState.phase_settings)) {
									const original = (originalPhases || []).find((p) => p.unique_setting_key === unique_row_key)
									if (original) {
										originalFrom = original.valid_from ?? null
										originalTo = original.valid_to ?? null
										break
									}
								}
							}

							phaseSettingsBySignal[signalId].push({
								phase_settings_id: phaseSettingId,
								valid_from: originalFrom ?? keyValidFrom ?? null,
								valid_to: originalTo ?? keyValidTo ?? 'infinity',
							})
						}
					}

					// Delete phase settings for each signal
					let totalDeleted = 0
					for (const [signalId, deleteData] of Object.entries(phaseSettingsBySignal)) {
						const response = await client(`/api/v1/pages/tms/phase-settings/signal/${signalId}/delete`, {
							method: 'POST',
							body: deleteData,
						})

						if (response.deleted && response.deleted.length > 0) {
							totalDeleted += response.deleted.length
						}
					}

					results.deletionResults.phase_settings.count += totalDeleted
					// Remove deleted phase settings from the UI lists
					this.removeDeletedPhaseSettingsFromUI(phaseSettingsForApi)

					// Trigger validation for all affected signals to check for dangling references
					for (const signalId of Object.keys(phaseSettingsBySignal)) {
						this.triggerTmsValidation(signalId)
					}
				}

				this.phase_settings_to_delete = []
			} catch (error) {
				console.error('Failed to delete phase settings:', error)
				results.deletionResults.phase_settings.success = false
				results.success = false
			}
		}

		// Delete coordination settings using new endpoint pattern
		if (this.coordination_settings_to_delete.length > 0) {
			try {
				// Separate coordination settings into those that need API calls vs direct deletion
				const coordSettingsForApi: string[] = []
				const coordSettingsForDirectDeletion: string[] = []

				for (const key of this.coordination_settings_to_delete) {
					const { id: coordSettingId } = this.parseDeleteKey(key)
					const isNewOrDefault = coordSettingId?.startsWith('new-') || coordSettingId?.startsWith('default-')

					if (isNewOrDefault) {
						coordSettingsForDirectDeletion.push(key)
					} else {
						coordSettingsForApi.push(key)
					}
				}

				// Handle direct deletion for new/default coordination settings
				if (coordSettingsForDirectDeletion.length > 0) {
					this.removeDeletedCoordinationSettingsFromUI(coordSettingsForDirectDeletion)
					results.deletionResults.coordination_settings.count += coordSettingsForDirectDeletion.length
				}

				// Handle API deletion for regular coordination settings
				if (coordSettingsForApi.length > 0) {
					// Group coordination settings by signal for the new endpoint structure
					const coordSettingsBySignal: { [signalId: string]: any[] } = {}

					for (const key of coordSettingsForApi) {
						const {
							id: coordSettingId,
							unique_row_key,
							valid_from: keyValidFrom,
							valid_to: keyValidTo,
						} = this.parseDeleteKey(key)
						// Find the signal that contains this specific coordination setting version
						let signalId: string | null = null
						for (const [sId, coords] of Object.entries(this.coordination_settings)) {
							const found = (coords || []).find((c) => {
								const cId = c.coordination_settings_id
								const cFrom = c.valid_from || null
								const cTo = c.valid_to == null ? 'infinity' : c.valid_to
								return (
									cId === coordSettingId &&
									(cFrom || null) === (keyValidFrom || null) &&
									cTo === (keyValidTo || 'infinity')
								)
							})
							if (found) {
								signalId = sId as string
								break
							}
						}
						if (signalId) {
							if (!coordSettingsBySignal[signalId]) {
								coordSettingsBySignal[signalId] = []
							}
							// For deletion, always use the original date range from page_start_states
							// so we target the row as it exists in the database, not any edited/archive range.
							let originalFrom: string | null = null
							let originalTo: string | null = null

							const coordStartState = this.page_start_states?.['coordination-settings'] as ISignalBaseState | undefined
							if (coordStartState?.coordination_settings) {
								for (const originalCoords of Object.values(coordStartState.coordination_settings)) {
									const original = (originalCoords || []).find((c) => c.unique_setting_key === unique_row_key)
									if (original) {
										originalFrom = original.valid_from ?? null
										originalTo = original.valid_to ?? null
										break
									}
								}
							}

							coordSettingsBySignal[signalId].push({
								coordination_settings_id: coordSettingId,
								valid_from: originalFrom ?? keyValidFrom ?? null,
								valid_to: originalTo ?? keyValidTo ?? 'infinity',
							})
						}
						// if (signalId) {
						// 	if (!coordSettingsBySignal[signalId]) {
						// 		coordSettingsBySignal[signalId] = [];
						// 	}
						// 	// For deletion, always use the original date range from page_start_states
						// 	// so we target the row as it exists in the database, not any edited/archive range.
						// 	let originalFrom: string | null = null;
						// 	let originalTo: string | null = null;

						// 	const sequencesStartState = this.page_start_states?.["sequences"] as ISignalBaseState | undefined;
						// 	if (sequencesStartState?.sequences) {
						// 		for (const originalSequences of Object.values(sequencesStartState.sequences)) {
						// 			const original = (originalSequences || []).find(
						// 				(c: any) => c.sequences_id === sequencesId,
						// 			);
						// 			if (original) {
						// 				originalFrom = original.valid_from ?? null;
						// 				originalTo = original.valid_to ?? null;
						// 				break;
						// 			}
						// 		}
						// 	}

						// 	coordSettingsBySignal[signalId].push({
						// 		sequences_id: sequencesId,
						// 		valid_from: originalFrom ?? keyValidFrom ?? null,
						// 		valid_to: originalTo ?? keyValidTo ?? "infinity",
						// 	});
						// }
					}

					// Delete coordination settings for each signal
					let totalDeleted = 0
					for (const [signalId, deleteData] of Object.entries(coordSettingsBySignal)) {
						const response = await client(`/api/v1/pages/tms/coordination-settings/signal/${signalId}/delete`, {
							method: 'POST',
							body: deleteData,
						})

						if (response.deleted && response.deleted.length > 0) {
							totalDeleted += response.deleted.length
						}
					}

					results.deletionResults.coordination_settings.count += totalDeleted
					// Remove deleted coordination settings from the UI lists
					this.removeDeletedCoordinationSettingsFromUI(coordSettingsForApi)

					// Trigger validation for all affected signals to check for dangling references
					for (const signalId of Object.keys(coordSettingsBySignal)) {
						this.triggerTmsValidation(signalId)
					}
				}

				this.coordination_settings_to_delete = []
			} catch (error) {
				console.error('Failed to delete coordination settings:', error)
				results.deletionResults.coordination_settings.success = false
				results.success = false
			}
		}

		// Delete phase mappings using new endpoint pattern
		if (this.phase_mappings_to_delete.length > 0) {
			try {
				// Group phase mappings by signal for the new endpoint structure
				const phaseMappingsBySignal: { [signalId: string]: any[] } = {}

				for (const key of this.phase_mappings_to_delete) {
					const { id: phaseMappingId, valid_from, valid_to } = this.parseDeleteKey(key)

					// Find the signal that contains this specific phase mapping version
					let signalId: string | null = null
					for (const [sId, mappings] of Object.entries(this.phase_mappings)) {
						const found = (mappings || []).find((m: any) => {
							const mId = m.atlas_phase_mapping_id
							const mFrom = m.valid_from || null
							const mTo = m.valid_to == null ? 'infinity' : m.valid_to
							return (
								mId === phaseMappingId && (mFrom || null) === (valid_from || null) && mTo === (valid_to || 'infinity')
							)
						})
						if (found) {
							signalId = sId as string
							break
						}
					}

					if (signalId) {
						if (!phaseMappingsBySignal[signalId]) phaseMappingsBySignal[signalId] = []
						phaseMappingsBySignal[signalId].push({
							atlas_phase_mapping_id: phaseMappingId,
							valid_from: valid_from || null,
							valid_to: valid_to || 'infinity',
						})
					}
				}

				// Delete phase mappings for each signal
				let totalDeleted = 0
				for (const [signalId, deleteData] of Object.entries(phaseMappingsBySignal)) {
					const response = await client(`/api/v1/pages/tms/phase-mappings/signal/${signalId}/delete`, {
						method: 'POST',
						body: deleteData,
					})

					if (response.deleted && response.deleted.length > 0) {
						totalDeleted += response.deleted.length
					}
				}

				results.deletionResults.phase_mappings.count = totalDeleted
				// Remove deleted phase mappings from the UI lists
				this.removeDeletedPhaseMappingsFromUI(this.phase_mappings_to_delete)
				this.phase_mappings_to_delete = []
			} catch (error) {
				console.error('Failed to delete phase mappings:', error)
				results.deletionResults.phase_mappings.success = false
				results.success = false
			}
		}

		if (this.sequences_to_delete.length > 0) {
			try {
				// Separate coordination settings into those that need API calls vs direct deletion
				const sequenceForApi: string[] = []
				const sequenceForDirectDeletion: string[] = []

				for (const key of this.sequences_to_delete) {
					const { id: sequenceId } = this.parseDeleteKey(key)
					const isNewOrDefault = sequenceId?.startsWith('new-') || sequenceId?.startsWith('default-')

					if (isNewOrDefault) {
						sequenceForDirectDeletion.push(key)
					} else {
						sequenceForApi.push(key)
					}
				}

				// Handle direct deletion for new/default coordination settings
				if (sequenceForDirectDeletion.length > 0) {
					this.removeDeletedSequencesFromUI(sequenceForDirectDeletion)
					results.deletionResults.sequences.count += sequenceForDirectDeletion.length
				}

				// Handle API deletion for regular coordination settings
				if (sequenceForApi.length > 0) {
					// Group coordination settings by signal for the new endpoint structure
					const sequencesBySignal: {
						[signalId: string]: {
							sequence_id: string
							valid_from: string | null
							valid_to: string
							valid_range: string
						}[]
					} = {}

					for (const key of sequenceForApi) {
						const { id: sequenceId, valid_from, valid_to } = this.parseDeleteKey(key)

						const toStartTS = (d: string | null) => `${d}T00:00:00.000Z`
						const toEndTS = (d?: string | null) => (d ? `${d}T23:59:59.999Z` : 'infinity')
						const valid_range = `["${toStartTS(valid_from)}", "${valid_to ? toEndTS(valid_to) : 'infinity'}")`
						// Find the signal that contains this specific coordination setting version
						let signalId: string | null = null

						for (const [signalIdCandidate, sequences_arr] of Object.entries(this.sequences)) {
							const found = (sequences_arr || []).some((sequenceRow) => {
								return sequenceRow.sequence_id === sequenceId
							})

							if (found) {
								signalId = signalIdCandidate as string
								break
							}
						}

						if (!signalId) {
							console.warn('Could not find signal for sequenceId:', sequenceId, 'key:', key)
							continue
						}

						if (!sequencesBySignal[signalId]) {
							sequencesBySignal[signalId] = []
						}

						sequencesBySignal[signalId].push({
							sequence_id: sequenceId,
							valid_from,
							valid_to: valid_to || 'infinity',
							valid_range,
						})
					}

					// Delete coordination settings for each signal
					let totalDeleted = 0
					for (const [signalId, deleteData] of Object.entries(sequencesBySignal)) {
						const response = await client(`/api/v1/pages/tms/sequences/signal/${signalId}/delete`, {
							method: 'POST',
							body: deleteData,
						})

						if (response.deleted && response.deleted.length > 0) {
							totalDeleted += response.deleted.length
						}
					}

					results.deletionResults.sequences.count += totalDeleted
					// Remove deleted coordination settings from the UI lists
					this.removeDeletedSequencesFromUI(sequenceForApi)
				}

				this.sequences_to_delete = []
			} catch (error) {
				console.error('Failed to delete coordination settings:', error)
				results.deletionResults.sequences.success = false
				results.success = false
			}
		}
	}

	/**
	 * Remove deleted TOD schedules from UI lists
	 * @param {Array} deletedKeys - Array of compound keys (id::from::to) that were successfully deleted
	 */
	removeDeletedTodsFromUI(deletedKeys: string[]) {
		// Remove from tod_schedules for each signal
		for (const signalId in this.tod_schedules) {
			if (this.tod_schedules[signalId]) {
				this.tod_schedules[signalId] = this.tod_schedules[signalId].filter((tod) => {
					const id = tod.id || tod.time_of_day_id
					const unique_key = tod.unique_setting_key
					const key = this.makeDeleteKey(id, unique_key, tod.valid_from || null, tod.valid_to || null)
					return !deletedKeys.includes(key)
				})
			}
		}
	}

	/**
	 * Remove deleted phase settings from UI lists
	 * @param {Array} deletedKeys - Array of compound keys (id::from::to) that were successfully deleted
	 */
	removeDeletedPhaseSettingsFromUI(deletedKeys: string[]) {
		// Remove from phase_settings for each signal
		for (const signalId in this.phase_settings) {
			if (this.phase_settings[signalId]) {
				this.phase_settings[signalId] = this.phase_settings[signalId].filter((setting) => {
					const key = this.makeDeleteKey(
						setting.phase_settings_id,
						setting.unique_setting_key,
						setting.valid_from || null,
						setting.valid_to || null,
					)
					return !deletedKeys.includes(key)
				})
			}
		}
	}

	/**
	 * Remove deleted coordination settings from UI lists
	 * @param {Array} deletedKeys - Array of compound keys (id::from::to) that were successfully deleted
	 */
	removeDeletedCoordinationSettingsFromUI(deletedKeys: string[]) {
		// Remove from coordination_settings for each signal
		for (const signalId in this.coordination_settings) {
			if (this.coordination_settings[signalId]) {
				this.coordination_settings[signalId] = this.coordination_settings[signalId].filter((setting) => {
					const key = this.makeDeleteKey(
						setting.coordination_settings_id,
						setting.unique_setting_key,
						setting.valid_from || null,
						setting.valid_to || null,
					)
					return !deletedKeys.includes(key)
				})
			}
		}
	}

	/**
	 * Remove deleted coordination settings from UI lists
	 * @param {Array} deletedKeys - Array of compound keys (id::from::to) that were successfully deleted
	 */
	removeDeletedSequencesFromUI(deletedKeys: string[]) {
		// Remove from coordination_settings for each signal
		for (const signalId in this.sequences) {
			if (this.sequences[signalId]) {
				this.sequences[signalId] = this.sequences[signalId].filter((setting) => {
					const key = this.makeDeleteKey(setting.sequence_id, '', setting.valid_from || null, setting.valid_to || null)
					return !deletedKeys.includes(key)
				})
			}
		}
	}

	/**
	 * Remove deleted phase mappings from UI lists
	 * @param {Array} deletedKeys - Array of compound keys (id::from::to) that were successfully deleted
	 */
	removeDeletedPhaseMappingsFromUI(deletedKeys: string[]) {
		// Remove from phase_mappings for each signal
		for (const signalId in this.phase_mappings) {
			if (this.phase_mappings[signalId]) {
				this.phase_mappings[signalId] = this.phase_mappings[signalId].filter((mapping) => {
					const id_to_check = mapping.atlas_phase_mapping_id
					const unique_setting_key = ''
					const key = this.makeDeleteKey(
						id_to_check || '',
						unique_setting_key,
						mapping.valid_from || null,
						mapping.valid_to || null,
					)
					return !deletedKeys.includes(key)
				})
			}
		}
	}

	/**
	 * Select a network and load its signals
	 * @param networkId - The ID of the network to select
	 * @returns The fetched signals
	 */
	async selectNetwork(networkId: string): Promise<SignalData[]> {
		if (this.selected_network === networkId && this.signals.length > 0) return this.signals

		this.selected_network = networkId
		this.selected_signal = null
		this.is_loading = true
		m.redraw()

		try {
			// Use the network specs endpoint to get signals
			const networkResponse = await client(`/api/v1/models/networks/${networkId}/specs`)

			// Extract signals from the response
			if (networkResponse && networkResponse.signals) {
				this.signals = networkResponse.signals || []
			} else {
				console.error('Invalid network response format:', networkResponse)
				this.signals = []
			}

			// Also fetch TOD schedules for this network
			try {
				// Use the network specs endpoint to get TOD schedules
				if (networkResponse && networkResponse.tods) {
					this.tod_schedules[networkId] = networkResponse.tods || []
				} else {
					// Fallback to direct TOD schedules endpoint if needed
					const todSchedules = await client(`/api/v1/models/networks/${networkId}/tod-schedules`)
					this.tod_schedules[networkId] = todSchedules || []
				}
			} catch (todError) {
				console.error('Failed to fetch TOD schedules:', todError)
				this.tod_schedules[networkId] = []
			}

			const signalsWithProgress = await this.createNetworkWideValidations(networkId, networkResponse.signals)
			this.signals = signalsWithProgress

			return signalsWithProgress
		} catch (error) {
			console.error('Failed to fetch signals:', error)
			this.signals = []
			throw error
		} finally {
			this.is_loading = false
			m.redraw()
		}
	}

	async createNetworkWideValidations(_networkId: string, currentSignals: SignalData[]) {
		currentSignals.forEach((signal) => {
			this.signal_errors[signal.id] = {
				'phase-settings': {
					errors: {},
					count: 0,
				},
				'coordination-settings': {
					errors: {},
					count: 0,
				},
				'controller-settings': {
					errors: {},
					count: 0,
				},
				'tod-schedule': {
					errors: {},
					count: 0,
				},
				'phase-mapping': {
					errors: {},
					count: 0,
				},
				sequences: {
					errors: {},
					count: 0,
				},
				'tms-errors': {
					errors: {},
					count: 0,
					progress: null,
				},
				'completed-configurations': {
					errors: {},
					count: 0,
				},
				'configuration-detials': {},
				totalCount: 0,
			}
		})

		// Fetch the whole network config from the new assembled-config endpoint (new schema) instead
		// of the legacy per-signal TMS endpoints, then reshape each signal into the legacy per-signal
		// arrays the validation + eligibility logic expects.
		const customer_id = getCustomerIdFromCurrentRoute()
		let network_config: INetworkOptimusConfiguration = []
		try {
			network_config = await fetchNetworkOptimusConfigOnce(customer_id, _networkId)
		} catch (error) {
			console.error('Failed to fetch network optimus configuration:', error)
			network_config = []
		}

		for (const signal of network_config) {
			const sid = signal.signal_id
			const mapped = mapNewConfigSignalToLegacy(signal)

			this.phase_settings[sid] = mapped.phase_settings as unknown as PhaseSettingsData[]
			this.coordination_settings[sid] = mapped.coordination_settings as unknown as CoordinationSettingsData[]
			this.controller_settings[sid] = mapped.controller_settings as unknown as ControllerSettingsData[]
			this.sequences[sid] = mapped.sequences as unknown as SequenceData[]
			this.tod_schedules[sid] = mapped.tod_schedules as unknown as TimeOfDayData[]
			this.phase_mappings[sid] = mapped.phase_mappings as unknown as PhaseMappingData[]

			// all_versions mirror the canonical maps — the new structure already carries every version.
			this.phase_settings_all_versions[sid] = this.phase_settings[sid]
			this.coordination_settings_all_versions[sid] = this.coordination_settings[sid]
			this.controller_settings_all_versions[sid] = this.controller_settings[sid]
			this.tod_schedules_all_versions[sid] = this.tod_schedules[sid]
			this.phase_mappings_all_versions[sid] = this.phase_mappings[sid]
		}

		const updatedSignals = currentSignals.map((signal) => {
			const progress = getSignalProgressStatus({
				phaseSettings: this.phase_settings[signal.id] || [],
				coordinationSettings: this.coordination_settings[signal.id] || [],
				timeOfDaySchedule: this.tod_schedules[signal.id] || [],
				phaseMapping: this.phase_mappings[signal.id] || [],
				sequences: this.sequences[signal.id] || [],
				controllerSettings: this.controller_settings[signal.id] || [],
				tms_errors: { errors: {}, count: 0, progress: null },
				signal: signal,
			})

			return {
				...signal,
				...progress,
			}
		})

		return updatedSignals
	}
	/**
	 * Select a signal and load its configuration data
	 * @param {string} signalId - The ID of the signal to select
	 * @returns {Promise<void>}
	 */
	async selectSignal(signalId: string) {
		if (this.selected_signal === signalId) return

		const CACHE_TTL_MS = 5 * 60 * 1000

		const hasCachedData =
			signalId in this.phase_settings ||
			signalId in this.coordination_settings ||
			signalId in this.tod_schedules ||
			signalId in this.phase_mappings ||
			signalId in this.sequences ||
			signalId in this.controller_settings

		const lastLoaded = this.signalLoadTimestamps[signalId]
		const isStale = !lastLoaded || Date.now() - lastLoaded > CACHE_TTL_MS

		const hasPendingChanges = this.signals_with_pending_changes?.has(signalId)

		if (hasCachedData && (!isStale || hasPendingChanges)) {
			// Use existing in-memory data; do not refetch to avoid nuking drafts/unsaved edits
			this.selected_signal = signalId
			this.is_loading = false
			m.redraw()
			return
		}

		this.selected_signal = signalId
		this.is_loading = true
		m.redraw()

		try {
			// Fetch core settings in parallel
			const [phaseSettings, coordinationSettings, todSchedules, phaseMappings, sequences]: [
				PhaseSettingsData[],
				CoordinationSettingsData[],
				TimeOfDayData[],
				PhaseMappingData[],
				SequenceData[],
			] = await Promise.all([
				this.fetchPhaseSettings(signalId).catch(() => {
					return []
				}),
				this.fetchCoordinationSettings(signalId).catch(() => {
					return []
				}),
				this.fetchTimeOfDaySchedules(signalId).catch(() => {
					if (this.selected_network) {
						return this.tod_schedules[this.selected_network] || []
					}
					return []
				}),
				this.fetchPhaseMappings(signalId).catch(() => {
					return []
				}),
				this.fetchSequences(signalId).catch(() => {
					return []
				}),
			])

			this.triggerTmsValidation(signalId)
			// Store the fetched data

			// const phase_settings_with_key: PhaseSettingsData[] = phaseSettings.map((setting) => {
			// 	return {
			// 		...setting,
			// 		unique_setting_key: uuid()
			// 	}
			// })
			const phase_settings_with_key: PhaseSettingsData[] = this.attachKeyToSettings(phaseSettings)
			const coordination_settings_with_key: CoordinationSettingsData[] = this.attachKeyToSettings(coordinationSettings)
			this.phase_settings[signalId] = phase_settings_with_key

			this.coordination_settings[signalId] = coordination_settings_with_key
			this.sequences[signalId] = sequences

			// Fetch controller settings separately to keep this change isolated
			const controllerSettings = await this.fetchControllerSettings(signalId).catch(() => {
				return []
			})
			this.controller_settings[signalId] = controllerSettings

			const todSchedulesWithKey = todSchedules.map((row) => {
				return {
					...row,
					key: this.generateKey(),
					valid_to: row.valid_to || 'infinity', // Convert null to infinity
				}
			})

			const tod_schedules_with_unique_key = this.attachKeyToSettings(todSchedulesWithKey)

			this.tod_schedules[signalId] = tod_schedules_with_unique_key

			// Store original TOD data for tracking changes
			this.storeOriginalTodData(signalId, todSchedulesWithKey)

			// Clear converted TODs tracking when loading fresh data
			this.clearConvertedTodsTracking()

			// Keep all phase mappings in the model (including empty ones) so we can track them for deletion
			// We'll filter out empty ones when saving to the server
			const phaseMappingsWithKeys = (phaseMappings || []).map((setting) => {
				const mappedPhases = setting.phase_mapping.map((phase, idx) => {
					const atomicMovementsWithKeys = phase.atomic_movements?.map((movement, idx) => {
						return {
							...movement,
							key: `${movement.edge_connection_id}-${idx}`,
						}
					})
					return {
						...phase,
						atomic_movements: atomicMovementsWithKeys,
						key: `${setting.atlas_phase_mapping_id}-${phase.number}=${idx}`,
					}
				})
				return {
					...setting,
					phase_mapping: mappedPhases,
				}
			})
			this.phase_mappings[signalId] = phaseMappingsWithKeys

			// Capture initial state once after loading data for all tabs
			this.captureStateForUndo('Signal selected - initial state', null)

			// Instrumentation: detect ghost phase mappings once per signal load
			this._logGhostPhaseMappings(signalId)

			// Capture page start states for all tabs when signal is first loaded
			const initialState = this.getCurrentState()
			this.page_start_states = {
				'phase-settings': this.deepCloneState(initialState),
				'coordination-settings': this.deepCloneState(initialState),
				'tod-schedule': this.deepCloneState(initialState),
				'phase-mapping': this.deepCloneState(initialState),
				'controller-settings': this.deepCloneState(initialState),
				sequences: this.deepCloneState(initialState),
			}

			this.signalLoadTimestamps[signalId] = Date.now()
		} catch (error) {
			// Set empty defaults
			this.phase_settings[signalId] = []
			this.coordination_settings[signalId] = []
			this.controller_settings[signalId] = []
			if (this.selected_network) {
				this.tod_schedules[signalId] = this.tod_schedules[this.selected_network] || []
			}
			this.phase_mappings[signalId] = []
			this.sequences[signalId] = []

			// Still capture page start states even with empty defaults
			const initialState = this.getCurrentState()
			this.page_start_states = {
				'phase-settings': this.deepCloneState(initialState),
				'coordination-settings': this.deepCloneState(initialState),
				'tod-schedule': this.deepCloneState(initialState),
				'phase-mapping': this.deepCloneState(initialState),
				'controller-settings': this.deepCloneState(initialState),
				sequences: this.deepCloneState(initialState),
			}

			this.signalLoadTimestamps[signalId] = Date.now()
		} finally {
			this.is_loading = false
			m.redraw()
		}
	}

	/**
	 * Set the active tab
	 * @param {string} tab - The tab to activate ('phase', 'coordination', or 'tod')
	 */
	setActiveTab(tab: string) {
		this.active_tab = tab
		m.redraw()
	}

	/**
	 * Fetch phase settings for a signal
	 * @param signalId - The ID of the signal
	 * @returns The fetched phase settings
	 */
	async fetchPhaseSettings(signalId: string): Promise<PhaseSettingsData[]> {
		try {
			// Use the TMS phase settings endpoint with archives=true to get all plans
			const response = await client(`/api/v1/pages/tms/phase-settings/signal/${signalId}?archives=true`)
			const rawSettings: PhaseSettingsData[] = response || []

			//console.log("PHASE SETTINGS MODEL DEBUG: fetchPhaseSettings raw", {
			//	signalId,
			//	count: rawSettings.length,
			//	summary: rawSettings.map((s: PhaseSettingsData, index: number) => ({
			//		index,
			//		phase_settings_id: s.phase_settings_id,
			//		valid_from: s.valid_from,
			//		valid_to: s.valid_to,
			//		id: s.id,
			//	})),
			//});

			// Ensure all settings have proper date fields for archiving
			const settingsWithDates = this.ensurePhaseSettingsDateFields(rawSettings)

			//console.log("PHASE SETTINGS MODEL DEBUG: fetchPhaseSettings processed", {
			//	signalId,
			//	count: settingsWithDates.length,
			//	summary: settingsWithDates.map((s: PhaseSettingsData, index: number) => ({
			//		index,
			//		phase_settings_id: s.phase_settings_id,
			//		valid_from: s.valid_from,
			//		valid_to: s.valid_to,
			//		id: s.id,
			//	})),
			//});

			return settingsWithDates
		} catch (error) {
			return []
		}
	}

	/**
	 * Clean up duplicate items in the model data
	 * This fixes the issue where items get duplicated during loading
	 */
	cleanupDuplicates() {
		// Helper function to get unique ID for different item types
		const getItemId = (item: any, category: string): string => {
			switch (category) {
				case 'phase_settings':
					return item.phase_settings_id
				case 'coordination_settings':
					return item.coordination_settings_id
				case 'tod_schedules':
					return item.time_of_day_id
				case 'phase_mappings':
					return item.atlas_phase_mapping_id
				case 'sequences':
					return item.id
				default:
					return item.id || item.phase_settings_id || item.coordination_settings_id || item.time_of_day_id
			}
		}

		// Clean up each category
		const categories = ['phase_settings', 'coordination_settings', 'tod_schedules', 'phase_mappings', 'sequences']

		categories.forEach((category) => {
			const categoryData = this[category as keyof OptimusConfigurationModel]
			if (categoryData) {
				Object.keys(categoryData).forEach((signalId) => {
					const items = categoryData[signalId]
					if (Array.isArray(items)) {
						// Remove duplicates by keeping only the first occurrence of each unique ID
						const seen = new Set()
						const deduplicated = items.filter((item: any) => {
							const id = getItemId(item, category)
							if (seen.has(id)) {
								return false
							}
							seen.add(id)
							return true
						})

						// Update the array with deduplicated items
						categoryData[signalId] = deduplicated
					}
				})
			}
		})
	}

	/**
	 * Archive a phase setting using the new archive endpoint
	 * @param signalId - The ID of the signal
	 * @param archiveData - The archive request data
	 * @returns The archived phase setting
	 */
	async archivePhaseSettings(
		signalId: string,
		archiveData: {
			phase_settings_id: string
			current_range: {
				valid_from: string | null
				valid_to: string | null
			}
			new_range: {
				valid_from: string
				valid_to: string
			}
		},
	): Promise<PhaseSettingsData> {
		// Model-only short-circuit: compute the archived item from the current
		// in-memory model and return it, without making any server calls.
		// Legacy server-backed implementation remains below but is now
		// unreachable; it will be removed once the deferred archive pipeline
		// is fully wired through applyAllChanges.
		const findMatchingSetting = (signalKey: string): PhaseSettingsData | undefined => {
			const settings = this.phase_settings?.[signalKey] || []
			return settings.find(
				(setting) =>
					setting.phase_settings_id === archiveData.phase_settings_id &&
					(setting.valid_from ?? null) === archiveData.current_range.valid_from &&
					(setting.valid_to ?? null) === archiveData.current_range.valid_to,
			)
		}

		// First try to find the item on the provided signalId
		let originalItem = findMatchingSetting(signalId)

		// As a defensive fallback (mirroring the prior implementation), search
		// across all signals if we didn't find a match for this signal.
		if (!originalItem && this.phase_settings) {
			for (const id of Object.keys(this.phase_settings)) {
				originalItem = findMatchingSetting(id)
				if (originalItem) break
			}
		}

		if (!originalItem) {
			console.error('PHASE ARCHIVE MODEL: Could not find phase setting to archive', {
				signalId,
				archiveData,
			})
			throw new Error(`Could not find phase setting to archive: ${archiveData.phase_settings_id}`)
		}

		const updatedItem: PhaseSettingsData = {
			...originalItem,
			valid_from: archiveData.new_range.valid_from,
			valid_to: archiveData.new_range.valid_to,
		}

		return updatedItem

		/* LEGACY server-backed archive implementation retained for reference

		// Check if this is a new or default phase setting
		const isNewOrDefault = archiveData.phase_settings_id?.startsWith("new-") ||
							   archiveData.phase_settings_id?.startsWith("default-");

		if (isNewOrDefault) {
			// Handle direct update for new/default phase settings
			const currentSettings = this.phase_settings[signalId] || [];
			const targetSetting = currentSettings.find(setting =>
				setting.phase_settings_id === archiveData.phase_settings_id &&
				setting.valid_from === archiveData.current_range.valid_from &&
				setting.valid_to === archiveData.current_range.valid_to
			);

			if (targetSetting) {
				// Update the object directly with new range values
				const updatedSetting = {
					...targetSetting,
					valid_from: archiveData.new_range.valid_from,
					valid_to: archiveData.new_range.valid_to,
				} as PhaseSettingsData;

				// Update the phase settings array
				this.phase_settings[signalId] = currentSettings.map(setting =>
					setting.phase_settings_id === archiveData.phase_settings_id &&
					setting.valid_from === archiveData.current_range.valid_from &&
					setting.valid_to === archiveData.current_range.valid_to
						? updatedSetting
						: setting
				);

				return updatedSetting;
			} else {
				throw new Error(`Could not find phase setting to archive: ${archiveData.phase_settings_id}`);
			}
		}

		try {
			// Send raw values to server - don't convert null to "infinity" here
			// The server needs the original null values to match against the database

			const response = await client(`/api/v1/pages/tms/phase-settings/signal/${signalId}/archive`, {
				method: "PUT",
				body: [archiveData], // Backend expects an array
			});


			if (response.archived && response.archived.length > 0) {
				const archivedItem = response.archived[0];

				// Check if server returned empty array (fallback for server-side lookup issues)
				if (Array.isArray(archivedItem) && archivedItem.length === 0) {
					console.warn(
						"🔍 PHASE ARCHIVE MODEL: Server returned empty array - using fallback with original data + new dates"
					);
					// Find the original item from our current model data
					// 	"PHASE ARCHIVE MODEL: Looking for phase_settings_id:",
					// 	archiveData.phase_settings_id
					// );
					// 	"PHASE ARCHIVE MODEL: Available signals in model:",
					// 	Object.keys(this.phase_settings || {})
					// );
					const phaseSettingsBySignal = this.phase_settings || {};
					const signalKey = Object.keys(phaseSettingsBySignal).find((id) =>
						phaseSettingsBySignal[id].some(
							(setting: PhaseSettingsData) =>
								setting.phase_settings_id === archiveData.phase_settings_id,
						),
					);
					const originalItem =
						signalKey != null
							? phaseSettingsBySignal[signalKey!].find(
									(setting: PhaseSettingsData) =>
										setting.phase_settings_id === archiveData.phase_settings_id &&
										setting.valid_from === (archiveData.current_range.valid_from || null) &&
										setting.valid_to === (archiveData.current_range.valid_to || null),
							  ) ?? null
							: null;

					if (originalItem) {
						// 	from: archiveData.new_range.valid_from,
						// 	to: archiveData.new_range.valid_to,
						// });
						const fallbackItem = {
							...originalItem,
							valid_from: archiveData.new_range.valid_from,
							valid_to: archiveData.new_range.valid_to,
						};
						return fallbackItem;
					}
				}


				// Trigger UI refresh to ensure components show updated data
				this.notifyDataRefresh?.();

				return archivedItem;
			}

			console.error("🔍 PHASE ARCHIVE MODEL: No archived item in response:", response);
			throw new Error("No archived item returned from server");
		} catch (error) {
			console.error("🔍 PHASE ARCHIVE MODEL: Server error caught:", error);
			console.warn(
				"🔍 PHASE ARCHIVE MODEL: Using fallback due to server error - creating archived item manually"
			);

			// Find the original item from our current model data
			const signalId = Object.keys(this.phase_settings).find((id) =>
				this.phase_settings[id].some((setting) => setting.phase_settings_id === archiveData.phase_settings_id)
			);
			const originalItem = signalId
				? this.phase_settings[signalId].find(
						(setting) => setting.phase_settings_id === archiveData.phase_settings_id
						&& setting.valid_from === (archiveData.current_range.valid_from || null)
						&& setting.valid_to === (archiveData.current_range.valid_to || null)
				  )
				: null;

			if (originalItem) {
				const fallbackItem = originalItem;
				return fallbackItem;
			} else {
				console.error("🔍 PHASE ARCHIVE MODEL: Could not find original item for fallback!");
				throw error;

			}
		}
			*/
	}

	/**
	 * Archive a coordination setting using the new archive endpoint
	 * @param signalId - The ID of the signal
	 * @param archiveData - The archive request data
	 * @returns The archived coordination setting
	 */
	async archiveCoordinationSettings(
		signalId: string,
		archiveData: {
			coordination_settings_id: string
			current_range: {
				valid_from: string | null
				valid_to: string | null
			}
			new_range: {
				valid_from: string
				valid_to: string
			}
		},
	): Promise<CoordinationSettingsData> {
		// Check if this is a new or default coordination setting
		const isNewOrDefault =
			archiveData.coordination_settings_id?.startsWith('new-') ||
			archiveData.coordination_settings_id?.startsWith('default-')

		if (isNewOrDefault) {
			// Handle direct update for new/default coordination settings
			const currentSettings = this.coordination_settings[signalId] || []
			const targetSetting = currentSettings.find(
				(setting) =>
					setting.coordination_settings_id === archiveData.coordination_settings_id &&
					setting.valid_from === archiveData.current_range.valid_from &&
					setting.valid_to === archiveData.current_range.valid_to,
			)

			if (targetSetting) {
				// Update the object directly with new range values
				const updatedSetting: CoordinationSettingsData = {
					...targetSetting,
					valid_from: archiveData.new_range.valid_from,
					valid_to: archiveData.new_range.valid_to,
				}

				// Update the coordination settings array
				this.coordination_settings[signalId] = currentSettings.map((setting) =>
					setting.coordination_settings_id === archiveData.coordination_settings_id &&
					setting.valid_from === archiveData.current_range.valid_from &&
					setting.valid_to === archiveData.current_range.valid_to
						? updatedSetting
						: setting,
				)

				return updatedSetting
			}

			throw new Error(`Could not find coordination setting to archive: ${archiveData.coordination_settings_id}`)
		}

		try {
			// Send raw values to server - don't convert null to "infinity" here
			// The server needs the original null values to match against the database
			const response = await client(`/api/v1/pages/tms/coordination-settings/signal/${signalId}/archive`, {
				method: 'PUT',
				body: [archiveData], // Backend expects an array
			})

			if (response.archived && response.archived.length > 0) {
				const archivedItem = response.archived[0]

				// Check if server returned empty array (fallback for server-side lookup issues)
				if (Array.isArray(archivedItem) && archivedItem.length === 0) {
					console.warn(
						'🔍 COORD ARCHIVE MODEL: Server returned empty array - using fallback with original data + new dates',
					)
					// Find the original item from our current model data
					const signalIdWithMatch = Object.keys(this.coordination_settings).find((id) =>
						this.coordination_settings[id].some(
							(setting) => setting.coordination_settings_id === archiveData.coordination_settings_id,
						),
					)
					const originalItem = signalIdWithMatch
						? this.coordination_settings[signalIdWithMatch].find(
								(setting) =>
									setting.coordination_settings_id === archiveData.coordination_settings_id &&
									setting.valid_from === (archiveData.current_range.valid_from || null) &&
									setting.valid_to === (archiveData.current_range.valid_to || null),
							)
						: null

					if (originalItem) {
						const fallbackItem: CoordinationSettingsData = {
							...originalItem,
							valid_from: archiveData.new_range.valid_from,
							valid_to: archiveData.new_range.valid_to,
						}
						return fallbackItem
					}
				}

				return archivedItem
			}

			console.error('🔍 COORD ARCHIVE MODEL: No archived item in response:', response)
			throw new Error('No archived item returned from server')
		} catch (error) {
			console.error('🔍 COORD ARCHIVE MODEL: Server error caught:', error)
			console.warn('🔍 COORD ARCHIVE MODEL: Using fallback due to server error - creating archived item manually')

			// Find the original item from our current model data
			const signalIdWithMatch = Object.keys(this.coordination_settings).find((id) =>
				this.coordination_settings[id].some(
					(setting) => setting.coordination_settings_id === archiveData.coordination_settings_id,
				),
			)
			const originalItem = signalIdWithMatch
				? this.coordination_settings[signalIdWithMatch].find(
						(setting) =>
							setting.coordination_settings_id === archiveData.coordination_settings_id &&
							setting.valid_from === (archiveData.current_range.valid_from || null) &&
							setting.valid_to === (archiveData.current_range.valid_to || null),
					)
				: null

			if (originalItem) {
				const fallbackItem: CoordinationSettingsData = {
					...originalItem,
					valid_from: archiveData.new_range.valid_from,
					valid_to: archiveData.new_range.valid_to,
				}
				return fallbackItem
			}

			console.error('🔍 COORD ARCHIVE MODEL: Could not find original item for fallback!')
			throw error
		}
	}

	/**
	 * Archive a time of day schedule using the new archive endpoint
	 * @param signalId - The ID of the signal
	 * @param archiveData - The archive request data
	 * @returns The archived time of day schedule
	 */
	async archiveTimeOfDaySchedules(
		signalId: string,
		archiveData: {
			time_of_day_id: string
			current_range: {
				valid_from: string | null
				valid_to: string | null
			}
			new_range: {
				valid_from: string
				valid_to: string
			}
		},
	): Promise<TimeOfDayData> {
		// Check if this is a new or default time-of-day schedule
		const isNewOrDefault =
			archiveData.time_of_day_id?.startsWith('new-') || archiveData.time_of_day_id?.startsWith('default-')

		if (isNewOrDefault) {
			// Handle direct update for new/default time-of-day schedules
			const currentSchedules = this.tod_schedules[signalId] || []
			const targetSchedule = currentSchedules.find(
				(schedule) =>
					schedule.time_of_day_id === archiveData.time_of_day_id &&
					schedule.valid_from === archiveData.current_range.valid_from &&
					schedule.valid_to === archiveData.current_range.valid_to,
			)

			if (targetSchedule) {
				// Update the object directly with new range values
				const updatedSchedule = {
					...targetSchedule,
					valid_from: archiveData.new_range.valid_from,
					valid_to: archiveData.new_range.valid_to,
				}

				// Update the time-of-day schedules array
				this.tod_schedules[signalId] = currentSchedules.map((schedule) =>
					schedule.time_of_day_id === archiveData.time_of_day_id &&
					schedule.valid_from === archiveData.current_range.valid_from &&
					schedule.valid_to === archiveData.current_range.valid_to
						? updatedSchedule
						: schedule,
				)

				return updatedSchedule
			} else {
				throw new Error(`Could not find time-of-day schedule to archive: ${archiveData.time_of_day_id}`)
			}
		}

		try {
			// Send raw values to server - don't convert null to "infinity" here
			// The server needs the original null values to match against the database
			const response = await client(`/api/v1/pages/tms/time-of-days/signal/${signalId}/archive`, {
				method: 'PUT',
				body: [archiveData], // Backend expects an array
			})

			if (response.archived && response.archived.length > 0) {
				const archivedItem = response.archived[0]

				// Check if server returned empty array (fallback for server-side lookup issues)
				if (Array.isArray(archivedItem) && archivedItem.length === 0) {
					console.warn(
						'🔍 TOD ARCHIVE MODEL: Server returned empty array - using fallback with original data + new dates',
					)
					// Find the original item from our current model data
					const signalId = Object.keys(this.tod_schedules).find((id) =>
						this.tod_schedules[id].some((setting) => setting.time_of_day_id === archiveData.time_of_day_id),
					)
					const originalItem = signalId
						? this.tod_schedules[signalId].find(
								(setting) =>
									setting.time_of_day_id === archiveData.time_of_day_id &&
									setting.valid_from === (archiveData.current_range.valid_from || null) &&
									setting.valid_to === (archiveData.current_range.valid_to || null),
							)
						: null

					if (originalItem) {
						const fallbackItem = {
							...originalItem,
							valid_from: archiveData.new_range.valid_from,
							valid_to: archiveData.new_range.valid_to,
						}
						return fallbackItem
					}
				}

				return archivedItem
			}

			console.error('🔍 TOD ARCHIVE MODEL: No archived item in response:', response)
			throw new Error('No archived item returned from server')
		} catch (error) {
			console.error('🔍 TOD ARCHIVE MODEL: Server error caught:', error)
			console.warn('🔍 TOD ARCHIVE MODEL: Using fallback due to server error - creating archived item manually')

			// Find the original item from our current model data
			const signalId = Object.keys(this.tod_schedules).find((id) =>
				this.tod_schedules[id].some((setting) => setting.time_of_day_id === archiveData.time_of_day_id),
			)
			const originalItem = signalId
				? this.tod_schedules[signalId].find(
						(setting) =>
							setting.time_of_day_id === archiveData.time_of_day_id &&
							setting.valid_from === (archiveData.current_range.valid_from || null) &&
							setting.valid_to === (archiveData.current_range.valid_to || null),
					)
				: null

			if (originalItem) {
				const fallbackItem = {
					...originalItem,
					valid_from: archiveData.new_range.valid_from,
					valid_to: archiveData.new_range.valid_to,
				}

				return fallbackItem
			} else {
				console.error('🔍 TOD ARCHIVE MODEL: Could not find original item for fallback!')
				throw error
			}
		}
	}

	/**
	 * Fetch coordination settings for a signal
	 * @param signalId - The ID of the signal
	 * @returns The fetched coordination settings
	 */
	async fetchCoordinationSettings(signalId: string): Promise<CoordinationSettingsData[]> {
		try {
			// Use the TMS coordination settings endpoint with archives=true to get all plans
			// This ensures validation logic has complete information about existing plans
			const response = await client(`/api/v1/pages/tms/coordination-settings/signal/${signalId}?archives=true`)
			const settings = response || []
			// Ensure all settings have proper date fields for archiving
			return this.ensureCoordinationSettingsDateFields(settings)
		} catch (error) {
			return []
		}
	}
	/**
	 * Fetch controller settings for a signal
	 * @param signalId - The ID of the signal
	 * @returns The fetched controller settings
	 */
	async fetchControllerSettings(signalId: string): Promise<ControllerSettingsData[]> {
		try {
			const response = await client(`/api/v1/pages/tms/controller-settings/signal/${signalId}?archives=true`)
			const settings = response || []
			// Ensure all settings have proper date fields for archiving
			return this.ensureControllerSettingsDateFields(settings)
		} catch (error) {
			return []
		}
	}

	/**
	 * Fetch time of day schedules for a signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Promise<Array>} - The fetched time of day schedules
	 */
	async fetchTimeOfDaySchedules(signalId: string): Promise<TimeOfDayData[]> {
		// Use the correct TMS time-of-days endpoint with archives=true to get all schedules
		const url = `/api/v1/pages/tms/time-of-days/signal/${signalId}?archives=true`

		try {
			const response = await client(url)
			const schedules = response || []
			// Ensure all schedules have proper date fields for archiving
			return this.ensureTimeOfDayDateFields(schedules)
		} catch (error) {
			return []
		}
	}

	/**
	 * Fetch phase mappings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Promise<Array>} - The fetched phase mappings
	 */
	async fetchPhaseMappings(signalId: string): Promise<PhaseMappingData[]> {
		// Use the correct TMS phase-mappings endpoint
		const url = `/api/v1/pages/tms/phase-mappings/signal/${signalId}`

		try {
			const response = await client(url)
			const mappings = response || []
			// Ensure all mappings have proper date fields
			return this.ensurePhaseMappingDateFields(mappings)
		} catch (error) {
			return []
		}
	}

	/**
	 * Fetch phase mappings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Promise<Array>} - The fetched phase mappings
	 */
	async fetchSequences(signalId: string): Promise<SequenceData[]> {
		// Use the correct TMS phase-mappings endpoint
		const url = `/api/v1/pages/tms/sequences/signal/${signalId}`

		try {
			const response = await client(url)
			const sequences = response || []
			// Ensure all sequences have proper date fields
			return this.ensureSequencesDateFields(sequences)
		} catch (error) {
			return []
		}
	}

	/**
	 * Refetch all configuration data for a signal from the server
	 * This ensures we have the latest server state after any save operation
	 * @param {string} signalId - The ID of the signal to refetch data for
	 * @returns {Promise<void>}
	 */
	async refetchSignalData(signalId: string, guardRev?: number): Promise<void> {
		try {
			// If a save started earlier and the local state has since changed, skip refetch
			if (typeof guardRev === 'number' && this._getDirtyRev?.(signalId) !== guardRev) {
				return
			}

			// Grab the current in-memory state so we can preserve any rows that have backend errors
			const previousPhaseSettings: PhaseSettingsData[] = this.phase_settings?.[signalId] || []
			const previousCoordinationSettings: CoordinationSettingsData[] = this.coordination_settings?.[signalId] || []
			const previousTodSchedules: TimeOfDayData[] = this.tod_schedules?.[signalId] || []
			const previousPhaseMappings: PhaseMappingData[] = this.phase_mappings?.[signalId] || []
			const previousControllerSettings: ControllerSettingsData[] = this.controller_settings?.[signalId] || []
			const previousSequences: SequenceData[] = this.sequences?.[signalId] || []

			// Fetch all data types in parallel

			const [phaseSettings, coordinationSettings, todSchedules, phaseMappings, controllerSettings, sequences] =
				await Promise.all([
					this.fetchPhaseSettings(signalId).catch((error) => {
						console.error('Failed to refetch phase settings:', error)
						return []
					}),
					this.fetchCoordinationSettings(signalId).catch((error) => {
						console.error('Failed to refetch coordination settings:', error)
						return []
					}),
					this.fetchTimeOfDaySchedules(signalId).catch((error) => {
						console.error('Failed to refetch TOD schedules:', error)
						return []
					}),
					this.fetchPhaseMappings(signalId).catch((error) => {
						console.error('Failed to refetch phase mappings:', error)
						return []
					}),
					this.fetchControllerSettings(signalId).catch((error) => {
						console.error('Failed to refetch controller settings:', error)
						return []
					}),
					this.fetchSequences(signalId).catch((error) => {
						console.error('Failed to refetch controller settings:', error)
						return []
					}),
				])

			// Preserve any locally-known rows that have backend errors but were not persisted
			const phaseSettingsWithErrorsToPreserve: PhaseSettingsData[] = previousPhaseSettings.filter(
				(setting) =>
					!!setting.error &&
					!phaseSettings.some((refetched) => refetched.phase_settings_id === setting.phase_settings_id),
			)
			const coordinationSettingsWithErrorsToPreserve: CoordinationSettingsData[] = previousCoordinationSettings.filter(
				(setting) =>
					!!setting.error &&
					!coordinationSettings.some(
						(refetched) => refetched.coordination_settings_id === setting.coordination_settings_id,
					),
			)
			const todSchedulesWithErrorsToPreserve: TimeOfDayData[] = previousTodSchedules.filter(
				(schedule) =>
					!!schedule.error && !todSchedules.some((refetched) => refetched.time_of_day_id === schedule.time_of_day_id),
			)
			const phaseMappingsWithErrorsToPreserve: PhaseMappingData[] = previousPhaseMappings.filter(
				(mapping) =>
					!!mapping.error && !phaseMappings.some((refetched) => refetched.phase_settings_id === mapping.phase_settings_id),
			)
			const controllerSettingsWithErrorsToPreserve: ControllerSettingsData[] = previousControllerSettings.filter(
				(setting) =>
					!!setting.error &&
					!controllerSettings.some((refetched) => refetched.controller_settings_id === setting.controller_settings_id),
			)
			const sequencesWithErrorsToPreserve: SequenceData[] = previousSequences.filter(
				(setting) => !!setting.error && !sequences.some((refetched) => refetched.sequence_id === setting.sequence_id),
			)

			const mergedPhaseSettings: PhaseSettingsData[] = [...phaseSettings, ...phaseSettingsWithErrorsToPreserve]
			const mergedCoordinationSettings: CoordinationSettingsData[] = [
				...coordinationSettings,
				...coordinationSettingsWithErrorsToPreserve,
			]
			const mergedTodSchedulesBase: TimeOfDayData[] = [...todSchedules, ...todSchedulesWithErrorsToPreserve]
			const mergedPhaseMappingsBase: PhaseMappingData[] = [...phaseMappings, ...phaseMappingsWithErrorsToPreserve]
			const mergedControllerSettings: ControllerSettingsData[] = [
				...controllerSettings,
				...controllerSettingsWithErrorsToPreserve,
			]
			const mergedSequences: SequenceData[] = [...sequences, ...sequencesWithErrorsToPreserve]

			// Update the model with fresh server data + any preserved error rows

			const phase_settings_with_key = this.attachKeyToSettings(mergedPhaseSettings)
			this.phase_settings[signalId] = phase_settings_with_key
			const initial_phase_settings = this.page_start_states?.['phase-settings'].phase_settings[signalId]
			if (initial_phase_settings) {
				this.page_start_states['phase-settings'].phase_settings[signalId] = phase_settings_with_key
			}

			const coordination_settings_with_key = this.attachKeyToSettings(mergedCoordinationSettings)

			this.coordination_settings[signalId] = coordination_settings_with_key
			const initial_coordination_settings =
				this.page_start_states?.['coordination-settings'].coordination_settings[signalId]
			if (initial_coordination_settings) {
				this.page_start_states['coordination-settings'].coordination_settings[signalId] = coordination_settings_with_key
			}

			this.sequences[signalId] = mergedSequences
			this.controller_settings[signalId] = this.ensureControllerSettingsDateFields(mergedControllerSettings)

			// Add UI keys to TOD schedules and ensure valid_to defaults
			const todSchedulesWithKey = mergedTodSchedulesBase.map((row) => ({
				...row,
				key: this.generateKey(),
				valid_to: row.valid_to || 'infinity', // Convert null to infinity
			}))

			const tod_schedules_with_unique_key = this.attachKeyToSettings(todSchedulesWithKey)

			const initial_tod_schedules = this.page_start_states?.['tod-schedule'].tod_schedules[signalId]
			if (initial_tod_schedules) {
				this.page_start_states['tod-schedule'].tod_schedules[signalId] = tod_schedules_with_unique_key
			}

			this.tod_schedules[signalId] = tod_schedules_with_unique_key
			// Store original TOD data for tracking changes
			this.storeOriginalTodData(signalId, todSchedulesWithKey)
			// Keep all phase mappings in the model (including empty ones) so we can track them for deletion
			// We'll filter out empty ones when saving to the server
			// Add keys to phase mappings for UI tracking (since fetchPhaseMappings doesn't add them)
			const phaseMappingsWithKeys = (mergedPhaseMappingsBase || []).map((setting) => {
				const mappedPhases = setting.phase_mapping.map((phase, idx) => {
					const atomicMovementsWithKeys = phase.atomic_movements?.map((movement, movementIdx) => ({
						...movement,
						key: `${movement.edge_connection_id}-${movementIdx}`,
					}))
					return {
						...phase,
						atomic_movements: atomicMovementsWithKeys,
						key: `${setting.atlas_phase_mapping_id}-${phase.number}=${idx}`,
					}
				})
				return {
					...setting,
					phase_mapping: mappedPhases,
				}
			})
			this.phase_mappings[signalId] = phaseMappingsWithKeys

			this.signalLoadTimestamps[signalId] = Date.now()

			// Instrumentation: detect ghost phase mappings after refetch
			this._logGhostPhaseMappings(signalId)

			// Clear the signal from pending changes since we now have fresh server data
			this.signals_with_pending_changes.delete(signalId)
			// Clear converted TODs tracking since we have fresh data
			this.clearConvertedTodsTracking()
			// Force UI redraw to show the fresh data
			m.redraw()
			// Trigger UI component refresh by notifying that data has been updated
			this.notifyDataRefresh?.()
			// IMPORTANT: Keep undo history spanning across saves
			// Capture a snapshot of the current state AFTER refetch if it differs from the last snapshot
			const latest = this.undo_history[this.undo_history.length - 1]
			const current = this.getCurrentState()
			if (!latest || !this.isStateEqual(current as ISignalBaseState, latest as ISignalBaseState)) {
				this.captureStateForUndo('Refetched from server', null)
			}
		} catch (error) {
			console.error(`Failed to refetch data for signal ${signalId}:`, error)
			throw error
		}
	}

	/**
	 * Get phase settings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Array} Array of phase settings
	 */
	getPhaseSettings(signalId: string) {
		if (!signalId) return []

		// Initialize phase_settings if it doesn't exist
		if (!this.phase_settings) {
			this.phase_settings = {}
		}

		// Return the phase settings for the specified signal
		return this.phase_settings[signalId] || []
	}

	/**
	 * Get coordination settings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Array} Array of coordination settings
	 */
	getCoordinationSettings(signalId: string) {
		if (!signalId) return []

		// Initialize coordination_settings if it doesn't exist
		if (!this.coordination_settings) {
			this.coordination_settings = {}
		}

		// Return the coordination settings for the specified signal
		return this.coordination_settings[signalId] || []
	}
	/**
	 * Get controller settings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Array} Array of controller settings
	 */
	getControllerSettings(signalId: string) {
		if (!signalId) return []

		// Initialize controller_settings if it doesn't exist
		if (!this.controller_settings) {
			this.controller_settings = {}
		}

		// Return the controller settings for the specified signal
		return this.controller_settings[signalId] || []
	}

	/**
	 * Get controller settings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Array} Array of controller settings
	 */
	getSequences(signalId: string) {
		if (!signalId) return []

		// Initialize controller_settings if it doesn't exist
		if (!this.sequences) {
			this.sequences = {}
		}

		// Return the controller settings for the specified signal
		return this.sequences[signalId] || []
	}

	/**
	 * Get time of day schedules for a signal from the model
	 * @param {string} signalId - The ID of the signal
	 * @returns {Array} - The time of day schedules
	 */
	getTimeOfDaySchedules(signalId?: string) {
		if (!signalId) {
			return []
		}
		// If we already have the schedules in the model, return them
		if (this.tod_schedules && this.tod_schedules[signalId]) {
			return this.tod_schedules[signalId]
		}

		// If we don't have the schedules yet, return an empty array
		// The caller should handle this by fetching the data if needed
		return []
	}

	/**
	 * Get phase mappings for a signal from the model
	 * @param {string} signalId - The ID of the signal
	 * @returns {Array} - The phase mappings
	 */
	getPhaseMappings(signalId: string) {
		// If we already have in the model, return them
		if (this.phase_mappings && this.phase_mappings[signalId]) {
			return this.phase_mappings[signalId]
		}

		// If we don't have yet, return an empty array
		// The caller should handle this by fetching the data if needed
		return []
	}

	/**
	 * Get a specific phase setting by its ID
	 * @param {string} settingsId - The ID of the phase setting
	 * @returns {Object|null} The phase setting object or null if not found
	 */
	getPhaseSettingById(settingsId: string) {
		for (const signalId in this.phase_settings) {
			const setting = this.phase_settings[signalId].find((s) => s.id === settingsId)
			if (setting) return setting
		}
		return null
	}

	/**
	 * Get a specific coordination setting by its ID
	 * @param {string} settingsId - The ID of the coordination setting
	 * @returns {Object|null} The coordination setting object or null if not found
	 */
	getCoordinationSettingById(settingsId: string) {
		for (const signalId in this.coordination_settings) {
			const setting = this.coordination_settings[signalId].find((s) => s.id === settingsId)
			if (setting) return setting
		}
		return null
	}

	/**
	 * Ensure coordination settings have proper date fields for archiving
	 * @param {Array} settings - Array of coordination settings
	 * @returns {Array} Settings with proper date fields
	 */
	ensureCoordinationSettingsDateFields(settings: CoordinationSettingsData[]) {
		return settings.map((setting) => {
			const result = { ...setting }

			// Convert null or missing valid_from to default
			if (!result.valid_from || result.valid_from == null) {
				result.valid_from = '2017-01-01' // Default start (far past)
			}

			// Convert null or missing valid_to to infinity
			if (!result.valid_to || result.valid_to == null) {
				result.valid_to = 'infinity' // Default end (infinite)
			}

			return result
		})
	}
	/**
	 * Ensure controller settings have proper date fields for archiving
	 * @param {Array} settings - Array of controller settings
	 * @returns {Array} Settings with proper date fields
	 */
	ensureControllerSettingsDateFields(settings: ControllerSettingsData[]) {
		return settings.map((setting) => {
			const result = { ...setting }

			// Convert null or missing valid_from to default
			if (!result.valid_from || result.valid_from == null) {
				result.valid_from = '2017-01-01' // Default start (far past)
			}

			// Convert null or missing valid_to to infinity
			if (!result.valid_to || result.valid_to == null) {
				result.valid_to = 'infinity' // Default end (infinite)
			}

			return result
		})
	}

	/**
	 * Ensure sequences have proper date fields for archiving
	 * @param {Array} settings - Array of sequences
	 * @returns {Array} Settings with proper date fields
	 */
	ensureSequencesDateFields(settings: SequenceData[]) {
		return settings.map((setting) => {
			const result = { ...setting }

			// Convert null or missing valid_from to default
			if (!result.valid_from || result.valid_from == null) {
				result.valid_from = '2017-01-01' // Default start (far past)
			}

			// Convert null or missing valid_to to infinity
			if (!result.valid_to || result.valid_to == null) {
				result.valid_to = 'infinity' // Default end (infinite)
			}

			return result
		})
	}

	/**
	 * Ensure time of day schedules have proper date fields for archiving
	 * @param {Array} schedules - Array of time of day schedules
	 * @returns {Array} Schedules with proper date fields
	 */
	ensureTimeOfDayDateFields(schedules: TimeOfDayData[]) {
		return schedules.map((schedule) => {
			const result = { ...schedule }

			// Convert null or missing valid_from to default
			if (!result.valid_from || result.valid_from === null) {
				result.valid_from = '2017-01-01' // Default start (far past)
			}

			// Convert null or missing valid_to to infinity
			if (!result.valid_to || result.valid_to === null) {
				result.valid_to = 'infinity' // Default end (infinite)
			}

			return result
		})
	}

	/**
	 * Ensure phase mappings have proper date fields
	 * @param {Array} mappings - Array of phase mappings
	 * @returns {Array} Mappings with proper date fields
	 */
	ensurePhaseMappingDateFields(mappings: PhaseMappingData[]) {
		return mappings.map((mapping) => {
			const result = { ...mapping }

			// Convert null or missing valid_from to default
			if (!result.valid_from || result.valid_from === null) {
				result.valid_from = '2017-01-01' // Default start (far past)
			}

			// Convert null or missing valid_to to infinity
			if (!result.valid_to || result.valid_to === null) {
				result.valid_to = 'infinity' // Default end (infinite)
			}

			return result
		})
	}

	/**
	 * Ensure phase settings have proper date fields for archiving
	 * @param {Array} settings - Array of phase settings
	 * @returns {Array} Settings with proper date fields
	 */
	ensurePhaseSettingsDateFields(settings: PhaseSettingsData[]) {
		return settings.map((setting) => {
			const result = { ...setting }

			// Set default valid_from if missing
			if (!result.valid_from) {
				result.valid_from = '2017-01-01' // Default start (far past)
			}

			// Set default valid_to if missing or null
			if (!result.valid_to || result.valid_to === null) {
				result.valid_to = 'infinity' // Default end (infinite)
			}

			return result
		})
	}

	/**
	 * Get a specific time-of-day schedule by its ID
	 * @param {string} todId - The ID of the time-of-day schedule
	 * @returns {Object|null} The time-of-day schedule object or null if not found
	 */
	getTimeOfDayById(todId: string) {
		for (const signalId in this.tod_schedules) {
			const schedule = this.tod_schedules[signalId].find((s) => s.id === todId)
			if (schedule) return schedule
		}
		return null
	}

	/**
	 * Get a specific phase_mapping by its ID
	 * @param {string} todId - The ID of the phase mapping
	 * @returns {Object|null} The phase mapping object or null if not found
	 */
	getPhaseMappingById(id: string) {
		for (const signalId in this.phase_mappings) {
			const pm = this.phase_mappings[signalId].find((pm) => pm.atlas_phase_mapping_id === id)
			if (pm) return pm
		}
		return null
	}

	/**
	 * Update coordination settings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @param {Array} settings - Array of coordination settings for the signal
	 * @returns {Promise<Array>} The updated coordination settings
	 */
	async updateSignalCoordinationSettings(
		signalId: string,
		settings: CoordinationSettingsData[],
		options?: { skipUndoCapture?: boolean },
	) {
		this.is_saving = true
		m.redraw()

		const skipUndoCapture = options?.skipUndoCapture === true

		try {
			// If we need to capture state (after an undo), capture the BEFORE state first
			if (this.needs_state_capture && !skipUndoCapture) {
				this.captureStateForUndo('Before coordination settings update (post-undo)', 'coordination-settings')
			}
			this.coordination_settings[signalId] = settings

			this.signals_with_pending_changes.add(signalId)

			this._bumpDirty?.(signalId)

			// NOTE: Autosave disabled for TODs - user must manually save
			this.scheduleAutoSave?.(signalId, 'coordination-setting')

			// Capture state AFTER making changes (normal case)
			if (!skipUndoCapture) {
				this.captureStateForUndo('Update coordination settings', 'coordination-settings')
			}

			// Trigger UI refresh to ensure components show updated data
			this.notifyDataRefresh?.()

			return this.coordination_settings[signalId] // Return the refetched data
		} catch (error) {
			throw error
		} finally {
			this.is_saving = false
			m.redraw()
		}
	}
	/**
	 * Update controller settings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @param {Array} settings - Array of controller settings for the signal
	 * @returns {Promise<Array>} The updated controller settings
	 */
	async updateSignalControllerSettings(
		signalId: string,
		settings: ControllerSettingsData[],
		options?: { skipUndoCapture?: boolean },
	) {
		this.is_saving = true
		m.redraw()

		const skipUndoCapture = options?.skipUndoCapture === true

		try {
			// If we need to capture state (after an undo), capture the BEFORE state first
			if (this.needs_state_capture && !skipUndoCapture) {
				this.captureStateForUndo('Before controller settings update (post-undo)', 'controller-settings')
			}

			this.controller_settings[signalId] = settings

			this.signals_with_pending_changes.add(signalId)

			this._bumpDirty?.(signalId)

			// Use a distinct key for autosave so caller can trace source
			this.scheduleAutoSave?.(signalId, 'controller-setting')

			// Capture state AFTER making changes (normal case)
			if (!skipUndoCapture) {
				this.captureStateForUndo('Update controller settings', 'controller-settings')
			}

			// Trigger UI refresh to ensure components show updated data
			this.notifyDataRefresh?.()

			return this.controller_settings[signalId]
		} catch (error) {
			throw error
		} finally {
			this.is_saving = false
			m.redraw()
		}
	}

	/**
	 * Update phase settings for a signal
	 * @param {string} signalId - The ID of the signal
	 * @param {Array} settings - Array of phase settings for the signal
	 * @returns {Promise<Array>} The updated phase settings
	 */
	async updateSignalPhaseSettings(signalId: string, settings: PhaseSettingsData[]) {
		//	signalId,
		//	settingsCount: settings.length,
		//	timestamp: new Date().toISOString(),
		//	stackTrace: new Error().stack,
		//});

		this.is_saving = true
		m.redraw()

		try {
			// If we need to capture state (after an undo), capture the BEFORE state first
			if (this.needs_state_capture) {
				this.captureStateForUndo('Before phase settings update (post-undo)', 'phase-settings')
			}

			// Ensure proper date fields before saving
			const processedPhaseSettings = this.ensurePhaseSettingsDateFields(settings)

			//console.log("PHASE SETTINGS MODEL DEBUG: updateSignalPhaseSettings processed", {
			//	signalId,
			//	processedCount: processedPhaseSettings.length,
			//	processedSummary: processedPhaseSettings.map((s: PhaseSettingsData, index: number) => ({
			//		index,
			//		phase_settings_id: s.phase_settings_id,
			//		valid_from: s.valid_from,
			//		valid_to: s.valid_to,
			//		id: s.id,
			//	})),
			//});

			this.phase_settings[signalId] = processedPhaseSettings

			//console.log("PHASE SETTINGS MODEL DEBUG: phase_settings[signalId] after assign", {
			//	signalId,
			//	modelCount: this.phase_settings[signalId]?.length ?? 0,
			//	modelSummary: (this.phase_settings[signalId] || []).map((s: PhaseSettingsData, index: number) => ({
			//		index,
			//		phase_settings_id: s.phase_settings_id,
			//		valid_from: s.valid_from,
			//		valid_to: s.valid_to,
			//		id: s.id,
			//	})),
			//});

			this.signals_with_pending_changes.add(signalId)

			this._bumpDirty?.(signalId)

			// NOTE: Autosave disabled for TODs - user must manually save
			// 	signalId,
			// 	autosaveEnabled: this.autosave?.enabled,
			// 	timestamp: new Date().toISOString(),
			// });
			this.scheduleAutoSave?.(signalId, 'phase-setting')

			this.captureStateForUndo('Update phase settings', 'phase-settings')

			// Trigger UI refresh to ensure components show updated data
			this.notifyDataRefresh?.()

			return this.phase_settings[signalId] // Return the refetched data
		} catch (error) {
			throw error
		} finally {
			this.is_saving = false
			m.redraw()
		}
	}

	/**
	 * Update signal TOD schedules in the model and mark for saving
	 * @param {string} signalId - The ID of the signal
	 * @param {Array} schedules - The updated TOD schedules
	 * @param {boolean} saveImmediately - Whether to save immediately or use debounced autosave
	 * @returns {Promise<boolean>} - Success status
	 */
	async updateSignalTodSchedules(
		signalId: string,
		schedules: TimeOfDayData[],
		_saveImmediately: boolean = false,
	): Promise<boolean> {
		// 	signalId,
		// 	scheduleCount: schedules.length,
		// 	schedules: schedules.map(s => ({
		// 		id: s.time_of_day_id,
		// 		startTime: s.time_of_day_data?.local_start_time,
		// 		endTime: s.time_of_day_data?.local_end_time
		// 	}))
		// });

		if (!this.signals_with_pending_changes) {
			this.signals_with_pending_changes = new Set()
		}

		if (!this.tod_schedules) {
			this.tod_schedules = {}
		}

		const schedules_with_external_ids = schedules.map((tod) => {
			const correct_coord_setting = (this.coordination_settings[signalId] || []).find(
				(coord) => coord.coordination_settings_id === tod.coordination_settings_id,
			)
			const correct_phase_setting = (this.phase_settings[signalId] || []).find(
				(phase_setting) => phase_setting.phase_settings_id === tod.phase_settings_id,
			)
			const updated_tod = {
				...tod,
			}
			if (correct_coord_setting) {
				updated_tod.coordination_settings_external_id = correct_coord_setting.coordination_settings_external_id
			}
			if (correct_phase_setting) {
				updated_tod.phase_settings_external_id = correct_phase_setting.phase_settings_external_id
			}
			return updated_tod
		})

		//	schedules: schedules_with_external_ids.map(s => ({
		//		id: s.time_of_day_id,
		//		startTime: s.time_of_day_data?.local_start_time,
		//		endTime: s.time_of_day_data?.local_end_time
		//	}))
		//});

		// Store the schedules directly without additional formatting
		this.tod_schedules[signalId] = schedules_with_external_ids
		this.signals_with_pending_changes.add(signalId)

		this._bumpDirty?.(signalId)

		// NOTE: Autosave disabled for TODs - user must manually save
		this.scheduleAutoSave?.(signalId, 'time-of-day')

		// this._bumpDirty?.(signalId);

		// NOTE: Autosave disabled for TODs - user must manually save
		// this.scheduleAutoSave(signalId);

		// Capture state AFTER making changes
		this.captureStateForUndo('Update TOD schedules', 'tod-schedule')

		// Trigger UI refresh even for local updates so the UI shows the changes immediately
		this.notifyDataRefresh?.()

		return true
	}

	/**
	 * Update signal phase mappings in the model and mark for saving
	 * @param {string} signalId - The ID of the signal
	 * @param {Array} phase_mappings - The updated phase_mappings
	 * @param {boolean} saveImmediately - Whether to save immediately or use debounced autosave
	 * @returns {Promise<boolean>} - Success status
	 */
	async updateSignalPhaseMappings(
		signalId: string,
		phase_mappings: PhaseMappingData[],
		saveImmediately: boolean = false,
	): Promise<boolean> {
		if (!this.signals_with_pending_changes) {
			this.signals_with_pending_changes = new Set()
		}

		if (!this.phase_mappings) {
			this.phase_mappings = {}
		}

		// If saving immediately, save to server and refetch
		if (saveImmediately) {
			try {
				// Filter out empty phase mappings ONLY when saving to server
				// Keep them in the model so deletion logic can find them
				const filteredPhaseMappings = phase_mappings.filter((setting) => setting.phase_mapping && setting.phase_mapping.length > 0)

				// Don't save if phase_mappings is empty - let delete handle it
				if (!filteredPhaseMappings || filteredPhaseMappings.length === 0) {
					return true
				}

				// Clean phase mappings for server (remove UI keys)
				const cleanedPhaseMappings = filteredPhaseMappings.map((setting) => {
					const cleanedPhases = setting.phase_mapping.map((phase) => {
						const cleanedMovements = phase.atomic_movements?.map(({ key, ...movement }) => movement) || []
						const { key, ...cleanedPhase } = phase
						return {
							...cleanedPhase,
							atomic_movements: cleanedMovements,
						}
					})
					return {
						...setting,
						phase_mapping: cleanedPhases,
						atlas_signal_id: signalId,
					}
				})

				// Save to server first
				await client(`/api/v1/pages/tms/phase-mappings/signal/${signalId}`, {
					method: 'PUT',
					body: cleanedPhaseMappings,
				})

				// If save was successful, refetch all data to get the latest server state

				// Capture state AFTER making changes
				this.captureStateForUndo('Update phase mappings', 'phase-mapping')

				return true
			} catch (error) {
				console.error('Failed to save phase mappings immediately:', error)
				throw error
			}
		} else {
			// Update model locally and use debounced autosave
			this._bumpDirty?.(signalId)
			this.scheduleAutoSave?.(signalId, 'phase-mapping')

			// Update the model with ALL phase mappings (including empty ones)
			// Empty ones are needed so deletion logic can find them
			// They'll be filtered out when saving to server
			this.phase_mappings[signalId] = phase_mappings
			this.signals_with_pending_changes.add(signalId)

			// Capture state AFTER making changes (this becomes the state to restore to)
			this.captureStateForUndo('After phase mappings update', 'phase-mapping')

			// Trigger UI refresh even for local updates so the UI shows the changes immediately
			this.notifyDataRefresh?.()

			return true
		}
	}

	/**
	 * Update signal TOD schedules in the model and mark for saving
	 * @param {string} signalId - The ID of the signal
	 * @param {Array} schedules - The updated TOD schedules
	 * @param {boolean} saveImmediately - Whether to save immediately or use debounced autosave
	 * @returns {Promise<boolean>} - Success status
	 */
	async updateSignalSequences(
		signalId: string,
		sequences: SequenceData[],
		options?: { skipUndoCapture?: boolean },
	): Promise<boolean> {
		this.is_saving = true
		m.redraw()

		const skipUndoCapture = options?.skipUndoCapture === true

		try {
			// If we need to capture state (after an undo), capture the BEFORE state first
			if (this.needs_state_capture && !skipUndoCapture) {
				this.captureStateForUndo('Before controller settings update (post-undo)', 'sequences')
			}

			this.sequences[signalId] = sequences

			this.signals_with_pending_changes.add(signalId)

			this._bumpDirty?.(signalId)

			// Use a distinct key for autosave so caller can trace source
			this.scheduleAutoSave?.(signalId, 'sequences')

			// Capture state AFTER making changes (normal case)
			if (!skipUndoCapture) {
				this.captureStateForUndo('Update sequences', 'sequences')
			}

			// Trigger UI refresh to ensure components show updated data
			this.notifyDataRefresh?.()

			return this.sequences[signalId]
		} catch (error) {
			throw error
		} finally {
			this.is_saving = false
			m.redraw()
		}
	}

	async handleApplyTodsAcrossSignals(signal_id: string, selected_signal_ids: Set<string>) {
		try {
			const signals_array = Array.from(selected_signal_ids)
			const signal_tods = this.getTimeOfDaySchedules(signal_id)
			const res = await applyTodsAcrossSignalsFn(signal_tods, signals_array)
			if (res?.length) {
				res.forEach((signal_data) => {
					const new_tods = signal_data.tods.map((tod_res) => tod_res.tod)
					const signal_id = signal_data.signal_id
					const schedules_with_external_ids = new_tods.map((tod) => {
						const correct_coord_setting = (this.coordination_settings[signal_id] || []).find(
							(coord) => coord.coordination_settings_id === tod.coordination_settings_id,
						)
						const correct_phase_setting = (this.phase_settings[signal_id] || []).find(
							(phase_setting) => phase_setting.phase_settings_id === tod.phase_settings_id,
						)
						const updated_tod = {
							...tod,
						}
						if (correct_coord_setting) {
							updated_tod.coordination_settings_external_id = correct_coord_setting.coordination_settings_external_id
						}
						if (correct_phase_setting) {
							updated_tod.phase_settings_external_id = correct_phase_setting.phase_settings_external_id
						}
						return updated_tod
					})

					this.tod_schedules[signal_id] = schedules_with_external_ids
				})

				notifications.addSuccess('New setting applied!')

				this.triggerTmsValidation(signal_id)
			}
		} catch (error) {
			notifications.addDanger('Something went wrong. Please try again...')
		}
	}
	async handleApplyCoordinationSettingsAcrossSignals(signal_id: string, selected_signal_ids: Set<string>) {
		try {
			const signals_array = Array.from(selected_signal_ids)
			const signal_coordination_settings = this.getCoordinationSettings(signal_id)
			const res = await applyCoordinationSettingsAcrossSignalsFn(signal_coordination_settings, signals_array)

			if (res?.length) {
				res.forEach((signal_data) => {
					const new_coordination_settings = signal_data.coordination_settings.map(
						(tod_res) => tod_res.coordination_setting,
					)
					const signal_id = signal_data.signal_id

					this.coordination_settings[signal_id] = new_coordination_settings
				})

				notifications.addSuccess('New setting applied!')

				this.triggerTmsValidation(signal_id)
			}
		} catch (error) {
			notifications.addDanger('Something went wrong. Please try again...')
		}
	}
	async handleApplyPhaseSettingsAcrossSignals(signal_id: string, selected_signal_ids: Set<string>) {
		try {
			const signals_array = Array.from(selected_signal_ids)
			const signal_phase_settings = this.getPhaseSettings(signal_id)
			const res = await applyPhaseSettingsAcrossSignalsFn(signal_phase_settings, signals_array)

			if (res?.length) {
				res.forEach((signal_data) => {
					const new_phase_settings = signal_data.phase_settings.map((tod_res) => tod_res.phase_setting)
					const signal_id = signal_data.signal_id

					this.phase_settings[signal_id] = new_phase_settings
				})

				notifications.addSuccess('New setting applied!')

				this.triggerTmsValidation(signal_id)
			}
		} catch (error) {
			notifications.addDanger('Something went wrong. Please try again...')
		}
	}
	async handleApplySequencesAcrossSignals(signal_id: string, selected_signal_ids: Set<string>) {
		try {
			const signals_array = Array.from(selected_signal_ids)
			const signal_sequences = this.getSequences(signal_id)
			const filtered_sequences = signal_sequences.filter((seq) => {
				const row_id = seq.sequence_id
				return !this.isMarkedForDeletion('sequences', row_id, '', seq.valid_from, seq.valid_to)
			})

			const res = await applySequencesAcrossSignalsFn(filtered_sequences, signals_array)

			if (res?.length) {
				res.forEach((signal_data) => {
					const new_sequences = signal_data.sequences.map((tod_res) => tod_res.sequence)
					const signal_id = signal_data.signal_id

					this.sequences[signal_id] = new_sequences
				})

				notifications.addSuccess('New setting applied!')

				this.triggerTmsValidation(signal_id)
			}
		} catch (error) {
			notifications.addDanger('Something went wrong. Please try again...')
		}
	}
	async handleApplyControllerSettingsAcrossSignals(signal_id: string, selected_signal_ids: Set<string>) {
		try {
			const signals_array = Array.from(selected_signal_ids)
			const signal_controller_settings = this.getControllerSettings(signal_id)
			const res = await applyControllerSettingsAcrossSignalsFn(signal_controller_settings, signals_array)

			if (res?.length) {
				res.forEach((signal_data) => {
					const new_controller_settings = signal_data.controller_settings.map((tod_res) => tod_res.controller_setting)
					const signal_id = signal_data.signal_id

					this.controller_settings[signal_id] = new_controller_settings
				})

				notifications.addSuccess('New setting applied!')

				this.triggerTmsValidation(signal_id)
			}
		} catch (error) {
			notifications.addDanger('Something went wrong. Please try again...')
		}
	}
	private computePhaseSettingsArchiveRequestsForSignal(signalId: string): PhaseSettingsArchiveRequest[] {
		const pageStartState = this.page_start_states?.['phase-settings'] as ISignalBaseState | undefined

		if (!pageStartState || !pageStartState.phase_settings) {
			return []
		}

		const originalForSignal: PhaseSettingsData[] = pageStartState.phase_settings?.[signalId] || []
		const currentSettings: PhaseSettingsData[] = this.phase_settings?.[signalId] || []

		if (originalForSignal.length === 0 || currentSettings.length === 0) {
			return []
		}

		const originalById = new Map<string, PhaseSettingsData>()

		for (const original of originalForSignal) {
			const id = original.phase_settings_id
			const unique_key = original.unique_setting_key
			if (!id) continue

			const isNewOrDefault = id.startsWith('new-') || id.startsWith('default-')
			if (isNewOrDefault) continue

			if (originalById.has(id)) {
				console.warn('PHASE ARCHIVE DIFF: Multiple original phase settings found for id; skipping diff', {
					signalId,
					phase_settings_id: id,
				})
				continue
			}

			// const id_with_start_and_end = `${id}-`

			originalById.set(unique_key, original)
		}

		const archiveRequests: PhaseSettingsArchiveRequest[] = []

		for (const [phaseSettingsKey, original] of originalById.entries()) {
			const current = currentSettings.find((setting) => setting.unique_setting_key === phaseSettingsKey)

			// If the plan no longer exists in the current state, it is handled by the
			// delete pipeline, not by archiving.
			if (!current) {
				continue
			}

			const origFrom = original.valid_from ?? null
			const origTo = original.valid_to ?? null
			const currFrom = current.valid_from ?? null
			const currTo = current.valid_to ?? null

			// If the date range didn't change, there's nothing to archive.
			if (origFrom === currFrom && origTo === currTo) {
				continue
			}

			const newValidFrom = currFrom ?? origFrom ?? '2017-01-01T00:00:00.000Z'
			const newValidTo = currTo ?? origTo ?? 'infinity'

			archiveRequests.push({
				phase_settings_id: current.phase_settings_id,
				current_range: {
					valid_from: origFrom,
					valid_to: origTo,
				},
				new_range: {
					valid_from: newValidFrom,
					valid_to: newValidTo,
				},
			})
		}

		return archiveRequests
	}

	private computeCoordinationSettingsArchiveRequestsForSignal(signalId: string): CoordinationSettingsArchiveRequest[] {
		const pageStartState = this.page_start_states?.['coordination-settings'] as ISignalBaseState | undefined

		if (!pageStartState || !pageStartState.coordination_settings) {
			return []
		}

		const originalForSignal: CoordinationSettingsData[] = pageStartState.coordination_settings?.[signalId] || []
		const currentSettings: CoordinationSettingsData[] = this.coordination_settings?.[signalId] || []

		if (originalForSignal.length === 0 || currentSettings.length === 0) {
			return []
		}

		const originalById = new Map<string, CoordinationSettingsData>()

		for (const original of originalForSignal) {
			const id = original.coordination_settings_id
			const unique_key = original.unique_setting_key
			if (!id) continue

			const isNewOrDefault = id.startsWith('new-') || id.startsWith('default-')
			if (isNewOrDefault) continue

			if (originalById.has(id)) {
				console.warn('COORD ARCHIVE DIFF: Multiple original coordination settings found for id; skipping diff', {
					signalId,
					coordination_settings_id: id,
				})
				continue
			}

			originalById.set(unique_key, original)
		}

		const archiveRequests: CoordinationSettingsArchiveRequest[] = []

		for (const [coordSettingsKey, original] of originalById.entries()) {
			const current = currentSettings.find((setting) => setting.unique_setting_key === coordSettingsKey)

			// If the plan no longer exists in the current state, it is handled by the
			// delete pipeline, not by archiving.
			if (!current) {
				continue
			}

			const origFrom = original.valid_from ?? null
			const origTo = original.valid_to ?? null
			const currFrom = current.valid_from ?? null
			const currTo = current.valid_to ?? null

			// If the date range didn't change, there's nothing to archive.
			if (origFrom === currFrom && origTo === currTo) {
				continue
			}

			const newValidFrom = currFrom ?? origFrom ?? '2017-01-01T00:00:00.000Z'
			const newValidTo = currTo ?? origTo ?? 'infinity'

			archiveRequests.push({
				coordination_settings_id: current.coordination_settings_id,
				current_range: {
					valid_from: origFrom,
					valid_to: origTo,
				},
				new_range: {
					valid_from: newValidFrom,
					valid_to: newValidTo,
				},
			})
		}

		return archiveRequests
	}

	private computeTodSchedulesArchiveRequestsForSignal(signalId: string): TimeOfDayArchiveRequest[] {
		const pageStartState = this.page_start_states?.['tod-schedule'] as ISignalBaseState | undefined

		if (!pageStartState || !pageStartState.tod_schedules) {
			return []
		}

		const originalForSignal: TimeOfDayData[] = pageStartState.tod_schedules?.[signalId] || []
		const currentSettings: TimeOfDayData[] = this.tod_schedules?.[signalId] || []

		if (originalForSignal.length === 0 || currentSettings.length === 0) {
			return []
		}

		const originalById = new Map<string, TimeOfDayData>()

		for (const original of originalForSignal) {
			const id = original.time_of_day_id
			const unique_key = original.unique_setting_key
			if (!id) continue

			const isNewOrDefault = id.startsWith('new-') || id.startsWith('default-')
			if (isNewOrDefault) continue

			if (originalById.has(id)) {
				console.warn('TOD ARCHIVE DIFF: Multiple original coordination settings found for id; skipping diff', {
					signalId,
					time_of_day_id: id,
				})
				continue
			}

			originalById.set(unique_key, original)
		}

		const archiveRequests: TimeOfDayArchiveRequest[] = []

		for (const [todScheduleKey, original] of originalById.entries()) {
			const current = currentSettings.find((setting) => setting.unique_setting_key === todScheduleKey)

			// If the plan no longer exists in the current state, it is handled by the
			// delete pipeline, not by archiving.
			if (!current) {
				continue
			}

			const origFrom = original.valid_from ?? null
			const origTo = original.valid_to ?? null
			const currFrom = current.valid_from ?? null
			const currTo = current.valid_to ?? null

			// If the date range didn't change, there's nothing to archive.
			if (origFrom === currFrom && origTo === currTo) {
				continue
			}

			const newValidFrom = currFrom ?? origFrom ?? '2017-01-01T00:00:00.000Z'
			const newValidTo = currTo ?? origTo ?? 'infinity'

			archiveRequests.push({
				time_of_day_id: current.time_of_day_id,
				current_range: {
					valid_from: origFrom,
					valid_to: origTo,
				},
				new_range: {
					valid_from: newValidFrom,
					valid_to: newValidTo,
				},
			})
		}

		return archiveRequests
	}

	private validatePhaseSettingsDateRangesForSignal(signalId: string): boolean {
		const currentPhaseSettingsForSignal = this.phase_settings?.[signalId] || []

		// No phase settings means nothing to validate.
		if (!currentPhaseSettingsForSignal || currentPhaseSettingsForSignal.length === 0) {
			return true
		}

		const signalErrors = this.signal_errors?.[signalId]
		if (!signalErrors) {
			// If we don't have an error bucket yet, allow the save to proceed rather than
			// blocking on validation we can't surface.
			return true
		}

		const phaseBucket = signalErrors['phase-settings'] || { errors: {}, count: 0 }
		signalErrors['phase-settings'] = phaseBucket

		const phaseErrors = (phaseBucket.errors || {}) as PhaseSettingsErrors
		phaseBucket.errors = phaseErrors

		// Clear any previous date-overlap errors for this signal so we don't show stale messages.
		for (const phaseSettingsId of Object.keys(phaseErrors)) {
			const errorByKey = phaseErrors[phaseSettingsId]
			if (!errorByKey) continue

			for (const key of Object.keys(errorByKey)) {
				if (key.startsWith('date-overlap')) {
					delete errorByKey[key]
				}
			}

			if (Object.keys(errorByKey).length === 0) {
				delete phaseErrors[phaseSettingsId]
			}
		}

		let hasErrors = false

		for (const plan of currentPhaseSettingsForSignal) {
			const planId = String(plan.phase_settings_id || plan.id || '')
			if (!planId) continue

			// Use the canonical date-only extraction so we are consistent with the
			// archive modal and other Optimus surfaces.
			const startDate = extractDateOnly(plan.valid_from ?? null) || '1900-01-01'
			const endDate = extractDateOnly(plan.valid_to ?? null)

			const message = validateDateRangeWithPlans(
				currentPhaseSettingsForSignal,
				startDate,
				endDate,
				planId,
				'phase',
				plan,
			)

			if (message) {
				hasErrors = true
				const planErrors = phaseErrors[planId] || {}
				planErrors['date-overlap'] = message
				phaseErrors[planId] = planErrors
			}
		}

		phaseBucket.count = Object.keys(phaseErrors).length
		this._recountTotals(signalId)

		if (hasErrors) {
			console.warn('🔎 PHASE DATE VALIDATION: Detected overlapping phase settings date ranges', {
				signalId,
				errors: phaseErrors,
			})
		}

		return !hasErrors
	}

	/**
	 * Apply all pending changes for all modified signals
	 * @returns {Promise} A promise that resolves when all changes are applied
	 */
	async applyAllChanges(guardRevisions?: Record<string, number>) {
		const signalsToUpdate = Array.from(this.signals_with_pending_changes)
		const hasDeletions =
			this.tods_to_delete.length > 0 ||
			this.phase_settings_to_delete.length > 0 ||
			this.coordination_settings_to_delete.length > 0 ||
			this.phase_mappings_to_delete.length > 0 ||
			this.sequences_to_delete.length > 0

		if (signalsToUpdate.length === 0 && !hasDeletions) {
			return { success: true, message: 'No changes to apply' }
		}

		this.is_saving = true
		m.redraw()

		try {
			const results: IApplyAllChangesResults = {
				success: true,
				failedSignals: [],
				successfulSignals: [],
				deletionResults: {
					tods: { success: true, count: 0 },
					phase_settings: { success: true, count: 0 },
					coordination_settings: { success: true, count: 0 },
					phase_mappings: { success: true, count: 0 },
					sequences: { success: true, count: 0 },
				},
			}

			// First, handle bulk deletions
			await this.processBulkDeletions(results)

			for (const signalId of signalsToUpdate) {
				try {
					let signalSuccess = true

					// Update TOD schedules if they exist
					if (this.tod_schedules && this.tod_schedules[signalId]) {
						try {
							const archiveRequests = this.computeTodSchedulesArchiveRequestsForSignal(signalId)

							if (archiveRequests.length > 0) {
								// 	signalId,
								// 	requestCount: archiveRequests.length,
								// 	requests: archiveRequests,
								// });

								const archiveResponse = await fetch(`/api/v1/pages/tms/time-of-days/signal/${signalId}/archive`, {
									method: 'PUT',
									headers: {
										'Content-Type': 'application/json',
										Accept: 'application/json',
									},
									body: JSON.stringify(archiveRequests),
									credentials: 'include',
								})

								const archiveText = await archiveResponse.text()
								let archiveParsed: any = archiveText

								try {
									archiveParsed = JSON.parse(archiveText)
								} catch {
									// keep archiveText as string
								}

								if (!archiveResponse.ok) {
									console.error('🔍 PHASE ARCHIVE DIFF: Archive request FAILED', {
										signalId,
										status: archiveResponse.status,
										errorText: archiveText,
										parsedBody: archiveParsed,
									})
									signalSuccess = false
									throw new Error(`Phase settings archive failed with status ${archiveResponse.status}: ${archiveText}`)
								} else {
									// 	signalId,
									// 	requestCount: archiveRequests.length,
									// 	responseSummary: archiveParsed,
									// });
								}
							}

							// Ensure TOD schedules have proper date fields
							const schedulesWithDates = this.ensureTimeOfDayDateFields(this.tod_schedules[signalId])

							const todResponse = await fetch(`/api/v1/pages/tms/time-of-days/signal/${signalId}`, {
								method: 'PUT',
								headers: {
									'Content-Type': 'application/json',
									Accept: 'application/json',
								},
								body: JSON.stringify(schedulesWithDates),
								credentials: 'include',
							})

							const todResponseText = await todResponse.text()
							let todParsedBody: IUpdateTimeOfDayRes | string
							try {
								// Backend should return { updated: TimeOfDayData[] }
								todParsedBody = JSON.parse(todResponseText)
							} catch (_err) {
								// Fallback in case server returns plain text
								todParsedBody = todResponseText
							}

							if (!todResponse.ok) {
								console.error('🔍 DOUBLE SAVE DEBUG: TOD schedules save FAILED', {
									signalId,
									status: todResponse.status,
									responseText: todResponseText,
								})
								signalSuccess = false
								throw new Error(`TOD update failed with status ${todResponse.status}: ${todResponseText}`)
							}

							// On success, update local model with server response so we capture any per-row errors
							if (typeof todParsedBody === 'object' && (todParsedBody as IUpdateTimeOfDayRes).updated) {
								const updatedTodSchedules = (todParsedBody as IUpdateTimeOfDayRes).updated

								const tod_schedules_with_key = this.attachKeyToSettings(updatedTodSchedules)
								this.tod_schedules[signalId] = tod_schedules_with_key

								const initial_tod_schedule = this.page_start_states?.['tod-schedule']?.tod_schedules?.[signalId]
								if (initial_tod_schedule) {
									this.page_start_states['tod-schedule'].tod_schedules[signalId] = tod_schedules_with_key
								}
								// 	signalId,
								// 	updatedTodSchedulesSummary: updatedTodSchedules.map((s) => ({
								// 		time_of_day_id: s.time_of_day_id,
								// 		name: s.name,
								// 		valid_from: s.valid_from,
								// 		valid_to: s.valid_to,
								// 		error: s.error,
								// 	})),
								// });
							}
						} catch (todError) {
							console.error('🔍 DOUBLE SAVE DEBUG: TOD schedules save exception', {
								signalId,
								error: todError,
							})
							signalSuccess = false
						}
					}

					// Update phase settings if they exist
					if (this.phase_settings && this.phase_settings[signalId]) {
						try {
							// Validate final phase settings date ranges before any archive/PUT calls.
							const isValidPhaseDates = this.validatePhaseSettingsDateRangesForSignal(signalId)
							if (!isValidPhaseDates) {
								console.warn('🔎 PHASE DATE VALIDATION: Blocking save due to overlapping date ranges', {
									signalId,
								})
								signalSuccess = false
								throw new Error(
									'Phase settings have overlapping date ranges. Please resolve the highlighted errors before saving.',
								)
							}
							// First, run any required archive operations derived from page_start_states -> current model
							const archiveRequests = this.computePhaseSettingsArchiveRequestsForSignal(signalId)
							if (archiveRequests.length > 0) {
								// 	signalId,
								// 	requestCount: archiveRequests.length,
								// 	requests: archiveRequests,
								// });

								const archiveResponse = await fetch(`/api/v1/pages/tms/phase-settings/signal/${signalId}/archive`, {
									method: 'PUT',
									headers: {
										'Content-Type': 'application/json',
										Accept: 'application/json',
									},
									body: JSON.stringify(archiveRequests),
									credentials: 'include',
								})

								const archiveText = await archiveResponse.text()
								let archiveParsed: any = archiveText

								try {
									archiveParsed = JSON.parse(archiveText)
								} catch {
									// keep archiveText as string
								}

								if (!archiveResponse.ok) {
									console.error('🔍 PHASE ARCHIVE DIFF: Archive request FAILED', {
										signalId,
										status: archiveResponse.status,
										errorText: archiveText,
										parsedBody: archiveParsed,
									})
									signalSuccess = false
									throw new Error(`Phase settings archive failed with status ${archiveResponse.status}: ${archiveText}`)
								} else {
									// 	signalId,
									// 	requestCount: archiveRequests.length,
									// 	responseSummary: archiveParsed,
									// });
								}
							}

							// Ensure phase settings have proper date fields
							const settingsWithDates = this.ensurePhaseSettingsDateFields(this.phase_settings[signalId])
							const filteredSettingsWithDates = settingsWithDates.filter(
								(setting: PhaseSettingsData, index: number) => {
									const hasId = Boolean(setting.phase_settings_id)
									const hasData = Boolean(setting.phase_settings_data)
									const hasExternalId = Boolean(setting.phase_settings_external_id)
									const hasSignalId = Boolean(setting.atlas_signal_id)
									const isValid = hasId || hasData || hasExternalId || hasSignalId

									if (!isValid) {
										console.warn(
											'[Optimus PhaseSettings] applyAllChanges: dropping invalid phase_settings row before save',
											{
												signalId,
												index,
												setting,
											},
										)
									}

									return isValid
								},
							)

							// 	signalId,
							// 	settingsCount: settingsWithDates.length,
							// 	settingsData: settingsWithDates,
							// 	timestamp: new Date().toISOString(),
							// });

							const phaseResponse = await fetch(`/api/v1/pages/tms/phase-settings/signal/${signalId}`, {
								method: 'PUT',
								headers: {
									'Content-Type': 'application/json',
									Accept: 'application/json',
								},
								body: JSON.stringify(filteredSettingsWithDates),
								credentials: 'include',
							})

							const responseText = await phaseResponse.text()
							let parsedBody

							try {
								parsedBody = JSON.parse(responseText)
							} catch (err) {
								parsedBody = responseText
							}

							if (!phaseResponse.ok) {
								console.error('🔍 DOUBLE SAVE DEBUG: Phase settings save FAILED', {
									signalId,
									status: phaseResponse.status,
									errorText: responseText,
								})
								signalSuccess = false
								throw new Error(`Phase settings update failed with status ${phaseResponse.status}: ${responseText}`)
							} else if (parsedBody && parsedBody.updated) {
								const updatedPhaseSettings: PhaseSettingsData[] = parsedBody.updated

								const valid_phase_settings = updatedPhaseSettings.filter((setting) => setting.phase_settings_id)
								const phase_settings_with_keys = this.attachKeyToSettings(valid_phase_settings)
								this.phase_settings[signalId] = phase_settings_with_keys

								const initial_phase_settings = this.page_start_states?.['phase-settings']?.phase_settings?.[signalId]
								if (initial_phase_settings) {
									this.page_start_states['phase-settings'].phase_settings[signalId] = phase_settings_with_keys
								}
								// this.page_start_states["phase-settings"] = []

								// Phase settings saved successfully
								// Note: Don't clear newly created tracking here - it should persist until modal closes
							}
						} catch (phaseError) {
							console.error('🔍 DOUBLE SAVE DEBUG: Phase settings save exception', {
								signalId,
								error: phaseError,
							})
							signalSuccess = false
						}
					}
					// Update controller settings if they exist
					if (this.controller_settings && this.controller_settings[signalId]) {
						try {
							// Ensure controller settings have proper date fields
							const controllerSettingsWithDates = this.ensureControllerSettingsDateFields(
								this.controller_settings[signalId],
							)
							const controllerResponse = await fetch(`/api/v1/pages/tms/controller-settings/signal/${signalId}`, {
								method: 'PUT',
								headers: {
									'Content-Type': 'application/json',
									Accept: 'application/json',
								},
								body: JSON.stringify(controllerSettingsWithDates),
								credentials: 'include',
							})
							const controllerResponseText = await controllerResponse.text()
							let controllerParsedBody
							try {
								controllerParsedBody = JSON.parse(controllerResponseText)
							} catch (err) {
								controllerParsedBody = controllerResponseText
							}
							if (!controllerResponse.ok) {
								signalSuccess = false
								throw new Error(
									`Controller settings update failed with status ${controllerResponse.status}: ${controllerResponseText}`,
								)
							} else if (controllerParsedBody && (controllerParsedBody as IUpdateControllerSettingsRes).updated) {
								const updatedControllerSettings = (controllerParsedBody as IUpdateControllerSettingsRes).updated
								this.controller_settings[signalId] = updatedControllerSettings
								// Controller settings saved successfully
							}
						} catch (controllerError) {
							console.error('🔍 DOUBLE SAVE DEBUG: Controller settings save exception', {
								signalId,
								error: controllerError,
							})
							signalSuccess = false
						}
					}

					// Update coordination settings if they exist
					if (this.coordination_settings && this.coordination_settings[signalId]) {
						try {
							// First, run any required archive operations derived from page_start_states -> current model
							const archiveRequests = this.computeCoordinationSettingsArchiveRequestsForSignal(signalId)

							if (archiveRequests.length > 0) {
								const archiveResponse = await fetch(
									`/api/v1/pages/tms/coordination-settings/signal/${signalId}/archive`,
									{
										method: 'PUT',
										headers: {
											'Content-Type': 'application/json',
											Accept: 'application/json',
										},
										body: JSON.stringify(archiveRequests),
										credentials: 'include',
									},
								)

								const archiveText = await archiveResponse.text()
								let archiveParsed: any = archiveText

								try {
									archiveParsed = JSON.parse(archiveText)
								} catch {
									// keep archiveText as string
								}

								if (!archiveResponse.ok) {
									console.error('50d COORD ARCHIVE DIFF: Archive request FAILED', {
										signalId,
										status: archiveResponse.status,
										errorText: archiveText,
										parsedBody: archiveParsed,
									})
									signalSuccess = false
									throw new Error(
										`Coordination settings archive failed with status ${archiveResponse.status}: ${archiveText}`,
									)
								}
							}

							// Ensure coordination settings have proper date fields
							const settingsWithDates = this.ensureCoordinationSettingsDateFields(this.coordination_settings[signalId])

							const coordResponse = await fetch(`/api/v1/pages/tms/coordination-settings/signal/${signalId}`, {
								method: 'PUT',
								headers: {
									'Content-Type': 'application/json',
									Accept: 'application/json',
								},
								body: JSON.stringify(settingsWithDates),
								credentials: 'include',
							})
							const responseText = await coordResponse.text()
							let parsedBody

							try {
								parsedBody = JSON.parse(responseText)
							} catch (err) {
								parsedBody = responseText
							}
							if (!coordResponse.ok) {
								signalSuccess = false
								throw new Error(
									`Coordination settings update failed with status ${coordResponse.status}: ${responseText}`,
								)
							} else if (parsedBody && parsedBody.updated) {
								const updatedCoordinationSettings = parsedBody.updated

								const coordination_settings_with_keys = this.attachKeyToSettings(updatedCoordinationSettings)
								this.coordination_settings[signalId] = updatedCoordinationSettings

								const initial_coordination_settings =
									this.page_start_states?.['coordination-settings'].coordination_settings[signalId]
								if (initial_coordination_settings) {
									this.page_start_states['coordination-settings'].coordination_settings[signalId] =
										coordination_settings_with_keys
								}
								// Coordination settings saved successfully
								// Note: Don't clear newly created tracking here - it should persist until modal closes
							}
						} catch (coordError) {
							signalSuccess = false
						}
					}

					// Update phase mappings if they exist
					if (this.phase_mappings && this.phase_mappings[signalId]) {
						try {
							const phase_mapping_with_dates = this.ensurePhaseMappingDateFields(this.phase_mappings[signalId])
							const phaseMappingWithoutKeys = phase_mapping_with_dates.map((setting) => {
								const cleanedPhases = setting.phase_mapping.map((phase) => {
									const cleanedMovements = phase.atomic_movements?.map(({ key, ...movement }) => movement) || []

									const { key, ...cleanedPhase } = phase
									return {
										...cleanedPhase,
										atomic_movements: cleanedMovements,
									}
								})

								return {
									...setting,
									phase_mapping: cleanedPhases,
								}
							})
							phase_mapping_with_dates.forEach((pm) => (pm.atlas_signal_id = signalId))
							phaseMappingWithoutKeys.forEach((pm) => (pm.atlas_signal_id = signalId))

							const coordResponse = await fetch(`/api/v1/pages/tms/phase-mappings/signal/${signalId}`, {
								method: 'PUT',
								headers: {
									'Content-Type': 'application/json',
									Accept: 'application/json',
								},
								body: JSON.stringify(phaseMappingWithoutKeys),
								credentials: 'include',
							})

							if (!coordResponse.ok) {
								const errorText = await coordResponse.text()
								signalSuccess = false
								throw new Error(`Phase Mappings update failed with status ${coordResponse.status}: ${errorText}`)
							}
						} catch (coordError) {
							signalSuccess = false
						}
					}

					if (this.sequences && this.sequences[signalId]) {
						try {
							const sequences_with_dates = this.ensureSequencesDateFields(this.sequences[signalId])

							const controllerResponse = await fetch(`/api/v1/pages/tms/sequences/signal/${signalId}`, {
								method: 'PUT',
								headers: {
									'Content-Type': 'application/json',
									Accept: 'application/json',
								},
								body: JSON.stringify(sequences_with_dates),
								credentials: 'include',
							})
							const controllerResponseText = await controllerResponse.text()
							let controllerParsedBody: string | null | IUpdateSequencesRes
							try {
								controllerParsedBody = JSON.parse(controllerResponseText)
							} catch (err) {
								controllerParsedBody = controllerResponseText
							}
							if (!controllerResponse.ok) {
								signalSuccess = false
								throw new Error(
									`Controller settings update failed with status ${controllerResponse.status}: ${controllerResponseText}`,
								)
							} else if (controllerParsedBody && (controllerParsedBody as IUpdateSequencesRes).updated) {
								const sequencesSettings = (controllerParsedBody as IUpdateSequencesRes).updated
								this.sequences[signalId] = sequencesSettings
								// Controller settings saved successfully
							}
						} catch (coordError) {
							signalSuccess = false
						}
					}

					if (signalSuccess) {
						results.successfulSignals.push(signalId)
						this.signals_with_pending_changes.delete(signalId)

						// Refetch all data for this signal to ensure we have the latest server state
						try {
							await this.refetchSignalData(signalId, guardRevisions?.[signalId])
							// 	signalId,
							// 	timestamp: new Date().toISOString(),
							// });
						} catch (refetchError) {
							console.error(`Failed to refetch data for signal ${signalId} after save:`, refetchError)
							// Don't fail the entire operation if refetch fails, but log it
						}
					} else {
						results.failedSignals.push(signalId)
						results.success = false
					}
				} catch (signalError) {
					results.failedSignals.push(signalId)
					results.success = false
				}
			}

			if (results.success) {
				let message = 'All changes saved successfully'
				const deletionCounts: string[] = []

				if (results.deletionResults.tods.count > 0) {
					deletionCounts.push(`${results.deletionResults.tods.count} TOD schedule(s) deleted`)
				}
				if (results.deletionResults.phase_settings.count > 0) {
					deletionCounts.push(`${results.deletionResults.phase_settings.count} phase setting(s) deleted`)
				}
				if (results.deletionResults.coordination_settings.count > 0) {
					deletionCounts.push(`${results.deletionResults.coordination_settings.count} coordination setting(s) deleted`)
				}
				if (results.deletionResults.phase_mappings.count > 0) {
					deletionCounts.push(`${results.deletionResults.phase_mappings.count} phase mapping(s) deleted`)
				}
				if (results.deletionResults.sequences.count > 0) {
					deletionCounts.push(`${results.deletionResults.sequences.count} sequence(s) deleted`)
				}

				if (deletionCounts.length > 0) {
					message += `. ${deletionCounts.join(', ')}.`
				}

				return { success: true, message }
			} else {
				const failureMessages: string[] = []

				if (results.failedSignals.length > 0) {
					failureMessages.push(`Failed to update signals: ${results.failedSignals.join(', ')}`)
				}

				if (!results.deletionResults.tods.success) {
					failureMessages.push('Failed to delete some TOD schedules')
				}
				if (!results.deletionResults.phase_settings.success) {
					failureMessages.push('Failed to delete some phase settings')
				}
				if (!results.deletionResults.coordination_settings.success) {
					failureMessages.push('Failed to delete some coordination settings')
				}
				if (!results.deletionResults.phase_mappings.success) {
					failureMessages.push('Failed to delete some phase mappings')
				}
				if (!results.deletionResults.sequences.success) {
					failureMessages.push('Failed to delete some sequences')
				}

				return {
					success: false,
					message: failureMessages.join('. '),
					failedSignals: results.failedSignals,
					successfulSignals: results.successfulSignals,
					deletionResults: results.deletionResults,
				}
			}
		} catch (error: any) {
			return {
				success: false,
				message: error.message || 'Failed to apply changes',
				error: error,
			}
		} finally {
			this.is_saving = false
			m.redraw()
		}
	}

	/**
	 * Apply changes for a specific signal
	 * @param {string} signalId - The ID of the signal
	 * @returns {Promise} A promise that resolves when changes for this signal are applied
	 */
	async applyChangesForSignal(signalId: string) {
		const promises: Promise<
			| IUpdatePhaseSettingsRes
			| IUpdateCoordinationSettingsRes
			| IUpdateTimeOfDayRes
			| IUpdatePhaseMappingRes
			| IUpdateControllerSettingsRes
		>[] = []

		// Apply controller settings changes if they exist
		if (this.controller_settings[signalId]) {
			// Ensure proper date fields before saving
			const processedControllerSettings = this.ensureControllerSettingsDateFields(this.controller_settings[signalId])
			promises.push(
				client(`/api/v1/pages/tms/controller-settings/signal/${signalId}`, {
					method: 'PUT',
					body: processedControllerSettings,
				})
					.then((response: IUpdateControllerSettingsRes) => {
						// Update local state with the response data that contains actual database IDs
						if (response && response.updated) {
							this.controller_settings[signalId] = response.updated
						}
						return response
					})
					.catch((error: string) => {
						console.error(`Failed to update controller settings for signal ${signalId}:`, error)
						return Promise.reject(error)
					}),
			)
		}

		// Apply coordination settings changes if they exist
		if (this.coordination_settings[signalId]) {
			promises.push(
				client(`/api/v1/pages/tms/coordination-settings/signal/${signalId}`, {
					method: 'PUT',
					body: this.coordination_settings[signalId],
				})
					.then((response: IUpdateCoordinationSettingsRes) => {
						// Update local state with the response data that contains actual database IDs
						if (response && response.updated) {
							this.coordination_settings[signalId] = response.updated
						}
						return response
					})
					.catch((error: string) => {
						console.error(`Failed to update coordination settings for signal ${signalId}:`, error)
						return Promise.reject(error)
					}),
			)
		}

		// Apply phase settings changes if they exist
		if (this.phase_settings[signalId]) {
			// Ensure proper date fields before saving
			const processedPhaseSettings = this.ensurePhaseSettingsDateFields(this.phase_settings[signalId])
			promises.push(
				client(`/api/v1/pages/tms/phase-settings/signal/${signalId}`, {
					method: 'PUT',
					body: processedPhaseSettings,
				})
					.then((response: IUpdatePhaseSettingsRes) => {
						// Update local state with the response data that contains actual database IDs
						if (response && response.updated) {
							this.phase_settings[signalId] = response.updated
						}
						return response
					})
					.catch((error: string) => {
						console.error(`Failed to update phase settings for signal ${signalId}:`, error)
						return Promise.reject(error)
					}),
			)
		}

		// Apply TOD schedule changes if they exist
		if (this.tod_schedules[signalId]) {
			promises.push(
				client(`/api/v1/pages/tms/tod-schedules/signal/${signalId}`, {
					method: 'PUT',
					body: this.tod_schedules[signalId],
				}).catch((error: string) => {
					console.error(`Failed to update TOD schedules for signal ${signalId}:`, error)
					return Promise.reject(error)
				}),
			)
		}

		// Apply Phase Mapping changes if they exist
		// Filter out any phase mappings with empty phases arrays - never send phase_mapping: [] to server
		if (this.phase_mappings[signalId] && this.phase_mappings[signalId].length > 0) {
			const validPhaseMappings = this.phase_mappings[signalId].filter((pm) => pm.phase_mapping && pm.phase_mapping.length > 0)

			// Only save if we have valid phase mappings after filtering
			if (validPhaseMappings.length > 0) {
				validPhaseMappings.forEach((pm) => (pm.atlas_signal_id = signalId))
				promises.push(
					client(`/api/v1/pages/tms/phase-mappings/signal/${signalId}`, {
						method: 'PUT',
						body: validPhaseMappings,
					}).catch((error: string) => {
						console.error(`Failed to update Phase Mappings for signal ${signalId}:`, error)
						return Promise.reject(error)
					}),
				)
			}
		}

		// Apply Sequences changes if they exist
		if (this.sequences[signalId]) {
			promises.push(
				client(`/api/v1/pages/tms/sequences/signal/${signalId}`, {
					method: 'PUT',
					body: this.sequences[signalId],
				}).catch((error: string) => {
					console.error(`Failed to update TOD schedules for signal ${signalId}:`, error)
					return Promise.reject(error)
				}),
			)
		}

		// Wait for all promises to resolve
		return Promise.all(promises)
	}

	/**
	 * Retry applying changes for failed signals
	 * @param {Array} failedSignals - Array of signal IDs that failed to update
	 * @param {number} batchSize - Number of requests to send in parallel (default: 5)
	 * @returns {Promise} A promise that resolves when retry is complete
	 */
	async retryFailedSignals(failedSignals: string[], batchSize = 5) {
		if (!failedSignals || failedSignals.length === 0) {
			return Promise.resolve({ success: true, retryCount: 0 })
		}

		this.is_saving = true
		m.redraw()

		const totalRetry = failedSignals.length
		let successCount = 0
		let stillFailedSignals: string[] = []

		try {
			// Process signals in batches
			for (let i = 0; i < totalRetry; i += batchSize) {
				const batch = failedSignals.slice(i, i + batchSize)

				// Create an array of promises for this batch
				const batchPromises = batch.map((signalId) => {
					return this.applyChangesForSignal(signalId)
						.then(() => {
							successCount++
							// Remove from pending changes if successful
							this.signals_with_pending_changes.delete(signalId)
						})
						.catch(() => {
							stillFailedSignals.push(signalId)
						})
				})

				// Wait for this batch to complete before starting the next one
				await Promise.allSettled(batchPromises)
			}

			return {
				success: stillFailedSignals.length === 0,
				totalRetry,
				successCount,
				stillFailedSignals,
			}
		} catch (error) {
			console.error('Failed to retry changes:', error)
			return {
				success: false,
				totalRetry,
				successCount,
				stillFailedSignals,
			}
		} finally {
			this.is_saving = false
			m.redraw()
		}
	}

	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//

	initAutosave(opts: Partial<IAutoSave> = {}): void {
		this.autosave = {
			delay_ms: opts.delay_ms ?? this.autosave.delay_ms,
			max_retries: opts.max_retries ?? this.autosave.max_retries,
			enabled: opts.enabled ?? true,
			timers: new Map<string, number>(),
			in_flight: new Set<string>(),
			needs_another_pass: new Set<string>(),
		}
		this.is_saving_by_signal ||= {}
		this.last_saved_at ||= {}
		this._dirty_revision ||= {}
	}

	enableAutosave(): void {
		this.autosave.enabled = true
	}
	disableAutosave(): void {
		this.autosave.enabled = false
	}

	private _bumpDirty(signalId: string): void {
		this._dirty_revision[signalId] = (this._dirty_revision[signalId] ?? 0) + 1
	}

	private _getDirtyRev(signalId: string): number {
		return this._dirty_revision[signalId] ?? 0
	}

	private _setSignalSaving(signalId: string, isSaving: boolean): void {
		this.is_saving_by_signal[signalId] = isSaving
		this.is_saving = Object.values(this.is_saving_by_signal).some(Boolean)
		m.redraw()
	}

	scheduleAutoSave(signalId: string, _reason = 'change'): void {
		if (!this.autosave.enabled) {
			return
		}

		const autosave_timer = this.autosave.timers.get(signalId)
		if (autosave_timer !== undefined) {
			//	signalId,
			//	timerId: autosave_timer,
			//});
			window.clearTimeout(autosave_timer)
		}

		const reasons_to_validate = ['time-of-day', 'phase-setting', 'coordination-setting']
		let should_validate_tms = false
		if (reasons_to_validate.includes(_reason)) {
			should_validate_tms = true
		}
		const id = window.setTimeout(() => {
			//	signalId,
			//	should_validate_tms,
			//	timestamp: new Date().toISOString(),
			//});
			void this._autosaveSignal(signalId, should_validate_tms)
		}, this.autosave.delay_ms)

		this.autosave.timers.set(signalId, id)
	}

	async flushAutoSave(signalId?: string): Promise<SaveResult | (SaveResult | null)[] | null> {
		if (!this.autosave.enabled) return null
		if (signalId) {
			const t = this.autosave.timers.get(signalId)
			if (t !== undefined) {
				window.clearTimeout(t)
				this.autosave.timers.delete(signalId)
			}
			return this._autosaveSignal(signalId)
		}

		const ids = Array.from(this.signals_with_pending_changes ?? new Set<string>())
		for (const sid of ids) {
			const t = this.autosave.timers.get(sid)
			if (t !== undefined) {
				window.clearTimeout(t)
				this.autosave.timers.delete(sid)
			}
		}
		return Promise.all(ids.map((sid) => this._autosaveSignal(sid)))
	}

	private async _autosaveSignal(signalId: string, should_validate_tms?: boolean): Promise<SaveResult | null> {
		if (!this.autosave.enabled) {
			return null
		}

		const t = this.autosave.timers.get(signalId)
		if (t !== undefined) {
			// 	signalId,
			// 	timerId: t,
			// });
			window.clearTimeout(t)
			this.autosave.timers.delete(signalId)
		}

		const has_pending_deletions =
			this.tods_to_delete.length > 0 ||
			this.phase_settings_to_delete.length > 0 ||
			this.coordination_settings_to_delete.length > 0 ||
			this.sequences_to_delete.length > 0 ||
			this.phase_mappings_to_delete.length > 0

		if (!this.signals_with_pending_changes?.has(signalId) && !has_pending_deletions) {
			return { success: true, skipped: true, message: 'No pending changes' }
		}

		if (this.autosave.in_flight.has(signalId)) {
			this.autosave.needs_another_pass.add(signalId)
			return { success: true, queued: true, message: 'Save in-flight; queued another pass' }
		}

		const startRev = this._getDirtyRev(signalId)

		const attemptSave = async (attempt: number): Promise<SaveResult> => {
			this.autosave.in_flight.add(signalId)
			this._setSignalSaving(signalId, true)

			try {
				const res = await this.applyAllChanges({ [signalId]: startRev })
				if (!res || !res.success) throw new Error('Autosave failed')

				if (this._getDirtyRev(signalId) === startRev) {
					this.signals_with_pending_changes.delete(signalId)
					this.last_saved_at[signalId] = Date.now()
				}

				this.autosave.in_flight.delete(signalId)
				this._setSignalSaving(signalId, false)

				if (this.autosave.needs_another_pass.delete(signalId) && this.signals_with_pending_changes.has(signalId)) {
					return attemptSave(0)
				}

				if (should_validate_tms) {
					this.triggerTmsValidation(signalId)
				}

				return res as unknown as SaveResult
			} catch (err) {
				if (attempt < this.autosave.max_retries) {
					const wait = Math.min(4000, 600 * Math.pow(2, attempt)) + Math.floor(Math.random() * 200)
					await new Promise((r) => window.setTimeout(r, wait))
					return attemptSave(attempt + 1)
				}
				this.autosave.in_flight.delete(signalId)
				this._setSignalSaving(signalId, false)
				return { success: false, error: err, message: (err as Error)?.message || 'Autosave failed' }
			}
		}

		return attemptSave(0)
	}
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//
	//

	triggerTmsValidation(signalId: string) {
		if (!this._debouncedTmsValidationMap.has(signalId)) {
			const fn = debounce(() => this._runTmsValidation(signalId), 5000, { leading: true, trailing: true })
			this._debouncedTmsValidationMap.set(signalId, fn)
		}
		this._debouncedTmsValidationMap.get(signalId)()
	}

	destroyAllTmsPoller() {
		for (const [, id] of Array.from(this._tmsPollers.entries())) {
			const tms_error_progress = {
				is_loading: false,
				total_count: 0,
				completed: 0,
			}
			if (id in this.tms_error_progress) {
				this.tms_error_progress[id] = tms_error_progress
			}
			clearInterval(id)
		}
		this._tmsPollers.clear()
		// optional: also cancel any pending debounced calls
		for (const [, fn] of Array.from(this._debouncedTmsValidationMap.entries())) fn?.cancel?.()
		this._debouncedTmsValidationMap.clear()
	}

	async _runTmsValidation(signalId: string) {
		const se = this.signal_errors?.[signalId]
		if (!se) return

		// ensure tms bucket
		const tms = (se['tms-errors'] ||= { errors: {}, count: 0, progress: null })
		if (!tms.errors) tms.errors = {}

		// build TOD → coord map, and clear old specialized errors for those coord IDs
		const tods = (this.tod_schedules?.[signalId] || []).filter((t) => t.coordination_settings_id && t.phase_settings_id)
		const coordIds = new Set(tods.map((t) => t.coordination_settings_id).filter(Boolean))
		Object.keys(tms.errors).forEach((k) => {
			const keyCoordId = k.split('-')[0]
			if ((coordIds.has(keyCoordId) || coordIds.has(keyCoordId)) && k.includes('-specialized-')) {
				delete tms.errors[k]
			}
		})

		/*
		try {
			const resp = await client({
				method: "GET",
				url: `/api/v1/pages/validation-alerts/check-tms-input-signal/${signalId}`,
				params: { reference_date: today },
			});

			// progress
			tms.progress = {
				completed: resp?.completed_results ?? 0,
				total: resp?.total_tods ?? 0,
				inProgress: resp?.in_progress_requests ?? 0,
				timestamp: resp?.timestamp,
			};

			// apply results now (if any)
			if (Array.isArray(resp?.results)) {
				this._mergeTmsResultsIntoBucket(tms, resp.results, todIdToCoord);
				this._recountTotals(signalId);
				m.redraw();
			}

			// schedule polling if needed
			const needsPolling =
				(resp?.in_progress_requests ?? 0) > 0 || (resp?.completed_results ?? 0) < (resp?.total_tods ?? 0);

			if (needsPolling && resp?.poll_url && !this._tmsPollers.has(signalId)) {
				const tms_error_progress = {
					is_loading: resp.completed_results !== resp.total_tods,
					total_count: resp.total_tods,
					completed: resp.completed_results,
				};
				this.tms_error_progress[signalId] = tms_error_progress;
				let attemptsLeft = 30; // 5 minutes @ 10s
				const tick = async () => {
					if (attemptsLeft-- <= 0) return this._stopPoll(signalId);
					try {
						const pr: ITmsValidationResponse = await client({ method: "GET", url: resp.poll_url });
						const tms_error_progress = {
							is_loading: pr.completed_results !== pr.total_tods,
							total_count: pr.total_tods,
							completed: pr.completed_results,
						};
						this.tms_error_progress[signalId] = tms_error_progress;
						tms.progress = {
							completed: pr?.completed_results ?? 0,
							total: pr?.total_tods ?? 0,
							inProgress: pr?.in_progress_requests ?? 0,
							timestamp: pr?.timestamp,
						};

						if (Array.isArray(pr?.results)) {
							this._mergeTmsResultsIntoBucket(tms, pr.results, todIdToCoord);
							this._recountTotals(signalId);
							m.redraw();
						}

						const done =
							((pr?.in_progress_requests ?? 0) === 0 &&
								(pr?.completed_results ?? 0) >= (pr?.total_tods ?? 0)) ||
							(pr.in_progress_requests === 0 && pr.new_requests_started === 0);
						if (done) this._stopPoll(signalId);
					} catch (e) {
						console.error("tms poll failed", e);
						// keep polling unless you want to stop on error
					}
				};
				const id = setInterval(tick, 10_000);
				this._tmsPollers.set(signalId, id);
				tick(); // immediate pulse
			}
		} catch (e) {
			console.error("tms initial fetch failed", e);
		} */
		this._stopPoll(signalId)
	}

	_stopPoll(signalId: string) {
		const id = this._tmsPollers.get(signalId)
		const tms_error_progress = {
			is_loading: false,
			total_count: 0,
			completed: 0,
		}
		this.tms_error_progress[signalId] = tms_error_progress
		if (id) {
			clearInterval(id)
			this._tmsPollers.delete(signalId)
		}
	}

	_recountTotals(signalId: string) {
		const se = this.signal_errors?.[signalId]
		if (!se) return
		se.totalCount =
			(se['phase-settings']?.count || 0) +
			(se['coordination-settings']?.count || 0) +
			(se['tod-schedule']?.count || 0) +
			(se['phase-mapping']?.count || 0) +
			(se['controller-settings']?.count || 0) +
			(se['sequences']?.count || 0) +
			(se['tms-errors']?.count || 0)
	}

	/**
	 * Mark a plan as newly created from "Set Upcoming Plan" modal
	 */
	markAsNewlyCreated(planId: string) {
		this.newly_created_plans.add(planId)
	}

	/**
	 * Check if a plan was newly created from "Set Upcoming Plan" modal
	 */
	isNewlyCreated(planId: string): boolean {
		return this.newly_created_plans.has(planId)
	}

	/**
	 * Remove a plan from the newly created tracking (e.g., after save)
	 */
	clearNewlyCreated(planId: string) {
		this.newly_created_plans.delete(planId)
	}

	/**
	 * Clear all newly created plan tracking
	 */
	clearAllNewlyCreated() {
		this.newly_created_plans.clear()
	}

	/**
	 * Clear newly created tracking for plans related to a specific signal
	 * This is useful when closing modals to remove "New" status
	 */
	clearNewlyCreatedForSignal(signalId: string) {
		// Get all plans for this signal and clear any that are marked as newly created
		const todSchedules = this.tod_schedules[signalId] || []
		const phaseSettings = this.phase_settings[signalId] || []
		const coordinationSettings = this.coordination_settings[signalId] || []

		// Clear TOD schedules
		todSchedules.forEach((schedule) => {
			if (schedule.time_of_day_id && this.isNewlyCreated(schedule.time_of_day_id)) {
				this.clearNewlyCreated(schedule.time_of_day_id)
			}
		})

		// Clear phase settings
		phaseSettings.forEach((setting) => {
			if (setting.phase_settings_id && this.isNewlyCreated(setting.phase_settings_id)) {
				this.clearNewlyCreated(setting.phase_settings_id)
			}
		})

		// Clear coordination settings
		coordinationSettings.forEach((setting) => {
			if (setting.coordination_settings_id && this.isNewlyCreated(setting.coordination_settings_id)) {
				this.clearNewlyCreated(setting.coordination_settings_id)
			}
		})
	}

	/**
	 * Clear converted TODs tracking when data is refreshed or signal changes
	 */
	clearConvertedTodsTracking() {
		this.converted_tods.clear()
	}

	/**
	 * Store original TOD data (day ranges AND start/end times) to track changes
	 */
	storeOriginalTodData(signalId: string, todSchedules: TimeOfDayData[]) {
		if (!this.original_tod_data[signalId]) {
			this.original_tod_data[signalId] = {}
		}

		todSchedules.forEach((tod) => {
			if (tod.time_of_day_data) {
				this.original_tod_data[signalId][tod.time_of_day_id] = {
					days: tod.time_of_day_data.days ? { ...tod.time_of_day_data.days } : null,
					local_start_time: tod.time_of_day_data.local_start_time,
					local_end_time: tod.time_of_day_data.local_end_time,
					mode: tod.time_of_day_data.mode, // Include mode in original data
				}
			}
		})
	}

	/**
	 * Check if a TOD schedule has ANY changes compared to original data
	 */
	hasTodDataChanged(signalId: string, todId: string, currentTodData: any): boolean {
		const originalData = this.original_tod_data[signalId]?.[todId]

		if (!originalData) {
			return true // If no original data, treat as changed to be safe
		}

		// Deep comparison of the entire TOD data structure
		const originalDataString = JSON.stringify(originalData)
		const currentDataString = JSON.stringify(currentTodData)
		const hasChanged = originalDataString !== currentDataString

		return hasChanged
	}

	/**
	 * Get TODs that need delete+recreate due to ANY data changes
	 * Only includes original TODs that haven't been converted yet
	 */
	getTodsWithDataChanges(signalId: string): TimeOfDayData[] {
		const currentTods = this.getTimeOfDaySchedules(signalId)

		const changedTods = currentTods.filter((tod) => {
			// Skip TODs that are already "new-" (they've already been converted)
			if (tod.time_of_day_id.includes('new-')) {
				return false
			}

			// Skip TODs that have already been converted in this session
			if (this.converted_tods.has(tod.time_of_day_id)) {
				return false
			}

			// Check if this TOD has any changes
			const hasChanges = this.hasTodDataChanged(signalId, tod.time_of_day_id, tod.time_of_day_data)

			return hasChanges
		})

		return changedTods
	}

	/**
	 * Delete TOD schedules using the new endpoint structure
	 */
	async deleteTimeOfDaySchedules(signalId: string, deleteData: any[]) {
		try {
			const response = await client(`/api/v1/pages/tms/time-of-days/signal/${signalId}/delete`, {
				method: 'POST',
				body: deleteData, // Array of {time_of_day_id, valid_from, valid_to}
			})

			if (response.deleted && response.deleted.length > 0) {
				return response.deleted
			}

			throw new Error('No deleted items returned from server')
		} catch (error) {
			console.error('Failed to delete time of day schedules:', error)
			throw error
		}
	}

	attachKeyToSettings<T extends object>(settings: T[]): T[] {
		const updated_settings = settings.map((setting) => {
			if (!('unique_setting_key' in setting)) {
				return {
					...setting,
					unique_setting_key: uuid(),
				}
			}
			return setting
		})

		return updated_settings
	}
}

// Export a singleton instance
const instance = new OptimusConfigurationModel()
export default instance
